import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import useAppStore from '../../store/appStore';
import { usePersonalDataSelector } from '../../store/usePersonalDataSelector';
import { downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';
import { getBarometerHourlyRatesFromAppStore } from '../../utils/pe-papers/peInvoiceSenderFromAppStore';
import {
    CONSULT_BUFFER_CLAUSE,
    CONSULT_CONDITION_OPTIONS,
    CONSULT_DEFAULT_DEPOSIT_CONFIG,
    CONSULT_FEASIBILITY_HINT,
    CONSULT_HAIR_BASE_OPTIONS,
    CONSULT_HOMECARE_CLAUSE,
    CONSULT_ORDER_CONFIRMATION,
    CONSULT_PFLEGE_GARANTIE_ACK,
    CONSULT_PFLEGE_GARANTIE_CLAUSE,
    CONSULT_PRIOR_OPTIONS,
    CONSULT_QUOTE_DOC_ID,
    CONSULT_QUOTE_SUBTITLE,
    CONSULT_QUOTE_TITLE,
    CONSULT_RISK_NOTICE,
    buildConsultMaterialSurchargeClause,
    calcConsultDepositAmountEur,
    getDefaultConsultationQuoteData,
    type ConsultFeasibility,
    type ConsultGarantieProdukt,
    type ConsultHairBase,
    type ConsultHairCondition,
    type ConsultLineItemId,
    type ConsultPriorTreatment,
    type ConsultStrandTest,
    type ConsultationQuoteFormData,
    type PeConsultationDepositConfig,
} from '../../utils/pe-papers/consultationQuoteCatalog';

export interface ConsultationQuoteModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: any;
    onArchived?: () => void;
}

const A4_PAGE_CLASS =
    'cq-a4-page bg-white text-black mx-auto w-full max-w-[210mm] min-h-[297mm] h-auto shadow-xl rounded-sm text-left block overflow-visible box-border my-4 print:my-0 print:shadow-none print:rounded-none';

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-3 py-2 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const LABEL_CLASS = 'text-[10px] font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';

const ERROR_RING = 'border-red-500 focus:border-red-500';

const STEP_LABELS: Record<number, string> = {
    1: 'Datenerfassung',
    2: 'Vorschau & Export',
};

/** Standard-MwSt. für Ausweis Netto / MwSt / Brutto (Beträge werden brutto erfasst). */
const CONSULT_VAT_RATE = 19;

/**
 * Nobel-Slate Optik — lesbar auf Ausdruck, trotzdem 2 A4 ohne PDF-Downscale.
 */
const CQ_DOC_CSS = `
  .cq-a4-page {
    font-family: "Segoe UI", Arial, Helvetica, sans-serif !important;
    font-size: 12px !important;
    line-height: 1.38 !important;
    color: #0f172a !important;
    background: #fff !important;
    box-sizing: border-box !important;
    padding: 10mm 11mm !important;
  }
  .cq-a4-page-2 {
    padding: 10mm 11mm !important;
    font-size: 12.5px !important;
    line-height: 1.35 !important;
  }
  .cq-screen-only { display: block; }
  .cq-page-label {
    font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
    color: #64748b; margin: 0 0 8px; padding-bottom: 4px; border-bottom: 1px dashed #cbd5e1;
  }
  .cq-header {
    width: 100%; border-collapse: collapse;
    margin: 0 0 10px; padding: 0 0 10px;
    border-bottom: 2px solid #E6C299;
  }
  .cq-header td { vertical-align: top; padding: 0; border: none; background: transparent; }
  .cq-title {
    font-size: 17px !important; font-weight: 800 !important; line-height: 1.2 !important;
    margin: 0 0 3px !important; text-align: left !important; color: #0f172a !important;
    background: none !important; border: none !important; text-transform: none !important;
    letter-spacing: normal !important; word-spacing: normal !important;
  }
  .cq-title-pe { font-style: italic; color: #E6C299 !important; font-weight: 800; }
  .cq-title-rest { color: #0f172a !important; font-weight: 800; }
  .cq-title-sub {
    display: block; font-size: 14px !important; font-weight: 700 !important;
    color: #0f172a !important; margin-top: 2px; letter-spacing: normal !important;
    text-transform: none !important;
  }
  .cq-salon-line {
    font-size: 12.5px; font-weight: 600; color: #334155; margin: 3px 0 0;
  }
  .cq-header-meta {
    width: 180px; text-align: right; font-size: 11px; color: #475569; line-height: 1.4;
    white-space: nowrap; padding-left: 12px !important;
  }
  .cq-header-meta p { margin: 0 0 3px !important; text-align: right !important; font-size: 11px !important; }
  .cq-header-meta strong { color: #0f172a; font-weight: 700; }
  .cq-lead {
    font-size: 12.5px; color: #64748b; margin: 0 0 10px; line-height: 1.4;
  }
  .cq-box {
    border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc;
    padding: 10px 12px; margin: 0 0 10px;
    page-break-inside: avoid !important; break-inside: avoid !important;
  }
  .cq-h {
    font-size: 12px !important; font-weight: 800 !important; text-transform: uppercase;
    letter-spacing: 0.05em; border-bottom: 1.5px solid #E6C299; padding-bottom: 4px;
    margin: 0 0 8px !important; background: none !important; color: #0f172a !important;
    text-align: left !important;
  }
  .cq-row { margin: 0 0 6px; font-size: 12px; color: #1e293b; }
  .cq-row strong { color: #0f172a; }
  .cq-check { margin: 0 0 4px; font-size: 12px; color: #1e293b; }
  .cq-table-wrap {
    overflow: hidden; border-radius: 8px; border: 1px solid #e2e8f0;
    background: #fff; margin: 0 0 10px;
  }
  .cq-table {
    width: 100%; border-collapse: collapse; margin: 0; font-size: 12.5px;
  }
  .cq-table th, .cq-table td {
    border: none; border-bottom: 1px solid #e2e8f0;
    padding: 8px 9px; text-align: left; vertical-align: top;
  }
  .cq-table th {
    background: #f1f5f9; font-weight: 800; font-size: 12.5px;
    text-transform: uppercase; letter-spacing: 0.05em; color: #475569;
  }
  .cq-table td { background: #fff; color: #1e293b; font-size: 12.5px; }
  .cq-table tr:last-child td { border-bottom: none; }
  .cq-table .cq-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .cq-table .cq-muted { font-size: 12.5px; color: #64748b; margin-top: 2px; display: block; }
  .cq-totals {
    margin: 0 0 10px 0; margin-left: auto; width: 100%; max-width: 260px;
    border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; padding: 10px 12px;
  }
  .cq-totals-row {
    display: flex; justify-content: space-between; align-items: baseline;
    gap: 10px; font-size: 12px; color: #475569; margin: 0 0 4px;
  }
  .cq-totals-row span:last-child { font-variant-numeric: tabular-nums; color: #0f172a; }
  .cq-totals-row.cq-totals-gross {
    margin-top: 6px; padding-top: 8px; border-top: 1px solid #cbd5e1;
    font-size: 14px; font-weight: 800; color: #0f172a;
  }
  .cq-totals-row.cq-totals-gross span { color: #0f172a; }
  .cq-legal { font-size: 12px; text-align: justify; margin: 0 0 7px; color: #475569; line-height: 1.35; }
  .cq-signal-box {
    border: 1.5px solid #94a3b8; border-radius: 8px; background: #f1f5f9;
    padding: 8px 10px; margin: 0 0 10px;
    page-break-inside: avoid !important; break-inside: avoid !important;
  }
  .cq-signal-box.cq-signal-accent {
    border-color: #c4a574; background: #faf6f0;
    border-left: 3.5px solid #E6C299;
  }
  .cq-signal-h {
    font-size: 11px !important; font-weight: 800 !important; text-transform: uppercase;
    letter-spacing: 0.06em; color: #0f172a !important; margin: 0 0 6px !important;
    padding: 0 0 4px; border-bottom: 1.5px solid #E6C299;
    background: none !important; text-align: left !important;
  }
  .cq-signal-amt {
    margin: 0 0 5px; font-size: 13px; color: #0f172a; line-height: 1.3;
  }
  .cq-signal-amt strong {
    font-weight: 800; font-variant-numeric: tabular-nums; font-size: 14px;
  }
  .cq-signal-note {
    margin: 0 0 4px; font-size: 12px; color: #334155; line-height: 1.35;
  }
  .cq-signal-note strong { color: #0f172a; font-weight: 700; }
  .cq-clause-list { margin: 0; padding: 0; }
  .cq-clause-list p {
    margin: 0 0 5px; font-size: 12px; color: #334155; line-height: 1.4;
    text-align: justify;
  }
  .cq-clause-list p:last-child { margin-bottom: 0; }
  .cq-clause-list strong { color: #0f172a; font-weight: 700; }
  .cq-sig-line { border-bottom: 1.5px solid #0f172a; height: 30px; margin-top: 5px; }
  .cq-sig-grid { display: table; width: 100%; margin-top: 10px; }
  .cq-sig-col { display: table-cell; width: 48%; padding-right: 4%; vertical-align: top; }
`;

/** PDF-Capture hat eigenes Außenpadding — Seiten-Padding daher 0, sonst Downscale. */
const CQ_PDF_CSS = `
  ${CQ_DOC_CSS}
  .cq-screen-only, .cq-page-label { display: none !important; }
  .cq-a4-page, .cq-a4-page-2 {
    min-height: 0 !important; margin: 0 !important; padding: 0 !important;
    box-shadow: none !important; max-width: none !important; border-radius: 0 !important;
  }
`;

/**
 * Browser-Druck = gleiche Basis-Optik wie PDF (CQ_PDF_CSS).
 * Die Anpassung auf exakt 1 A4 pro Abschnitt macht handlePrint
 * dynamisch (wie PDF fitSectionsToPage) — kein fester Zoom.
 */
const CQ_PRINT_CSS = `
  ${CQ_PDF_CSS}
  @page { size: A4; margin: 10mm; }
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .cq-a4-page, .cq-a4-page-2 {
    padding: 0 !important;
    margin: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    overflow: visible !important;
    box-shadow: none !important;
    transform-origin: top left;
  }
  .print-break, .pepapers-print-break {
    page-break-before: always !important;
    break-before: page !important;
  }
  .cq-box, .cq-table-wrap, .cq-totals, .cq-header, .cq-sig-grid, .cq-signal-box {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  .cq-screen-only, .cq-page-label { display: none !important; }
`;

const isEmpty = (v: string) => !String(v || '').trim();

const formatGermanDate = (iso: string) => {
    if (!iso) return '____________';
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return iso;
    return `${d}.${m}.${y}`;
};

const mark = (on: boolean) => (on ? '[X]' : '[ ]');

type TimeUnit = 'min' | 'std';

const LINE_RATE_MODE: Record<ConsultLineItemId, 'exclusive' | 'inclusive' | 'manual'> = {
    beratung_vorbereitung: 'exclusive',
    farb_blondiertechnik: 'inclusive',
    pflege_bondbuilder: 'inclusive',
    schnitt_styling: 'exclusive',
    heimpflege_material: 'manual',
};

const STANDARD_LINE_IDS: ConsultLineItemId[] = [
    'beratung_vorbereitung',
    'farb_blondiertechnik',
    'pflege_bondbuilder',
    'schnitt_styling',
    'heimpflege_material',
];

const getRateMode = (id: string): 'exclusive' | 'inclusive' | 'manual' => {
    if (id in LINE_RATE_MODE) return LINE_RATE_MODE[id as ConsultLineItemId];
    // Zusatzpositionen: Zeit × Satz ohne Material (manuell überschreibbar)
    return 'exclusive';
};

const isStandardLineId = (id: string) =>
    (STANDARD_LINE_IDS as string[]).includes(id);

const formatEuroDe = (n: number) =>
    n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Beträge immer auf volle Euro aufrunden (z. B. 22,26 → 23). */
const formatEuroCeilDe = (n: number) => {
    if (!(n > 0)) return '';
    const ceiled = Math.ceil(n - 1e-9);
    return ceiled.toLocaleString('de-DE', { maximumFractionDigits: 0 });
};

const parseEuroDe = (raw: string): number => {
    const cleaned = String(raw || '')
        .trim()
        .replace(/\s/g, '')
        .replace(/€/g, '')
        .replace(/\./g, '')
        .replace(',', '.');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
};

const parseTimeNumber = (raw: string): number => {
    const cleaned = String(raw || '')
        .trim()
        .replace(',', '.');
    const n = Number(cleaned);
    return Number.isFinite(n) && n > 0 ? n : 0;
};

const formatTimeEffort = (raw: string, unit: TimeUnit): string => {
    const n = parseTimeNumber(raw);
    if (!(n > 0)) return '';
    if (unit === 'min') return `${Math.round(n)} Min.`;
    const de = String(n).replace('.', ',');
    return `${de} Std.`;
};

const toHours = (raw: string, unit: TimeUnit): number => {
    const n = parseTimeNumber(raw);
    if (!(n > 0)) return 0;
    return unit === 'min' ? n / 60 : n;
};

const calcLineAmount = (
    id: string,
    timeRaw: string,
    unit: TimeUnit,
    rates: { exclusive: number; inclusive: number }
): string => {
    const mode = getRateMode(id);
    if (mode === 'manual') return '';
    const hours = toHours(timeRaw, unit);
    if (!(hours > 0)) return '';
    const rate = mode === 'inclusive' ? rates.inclusive : rates.exclusive;
    if (!(rate > 0)) return '';
    return formatEuroCeilDe(hours * rate);
};

const getBarometer10gRate = (kalk: any): number => {
    const list = Array.isArray(kalk?.priceParameterListe) ? kalk.priceParameterListe : [];
    const by10g = list.find((p: any) => /10\s*g/i.test(String(p?.desc || '')));
    if (by10g && Number(by10g.preis) > 0) return Number(by10g.preis);
    const byId = list.find((p: any) => p?.id === 'p1');
    if (byId && Number(byId.preis) > 0) return Number(byId.preis);
    const byMat = list.find((p: any) => {
        const d = String(p?.desc || '').toLowerCase();
        return (d.includes('material') || d.includes('farbe')) && Number(p?.preis) > 0;
    });
    return byMat ? Number(byMat.preis) : 0;
};

const sumQuoteTotalNumber = (pricing: {
    lineItems: { id: string; amountEur: string; inclusive?: boolean }[];
    bondProductInclusive: boolean;
    bondProductAmountEur: string;
    salesProducts: { amountEur: string; inclusive?: boolean }[];
    hasPflegeGarantie?: boolean;
    garantiePflegesetInGesamtpreis?: boolean;
    garantieProdukte?: { preisBrutto?: number }[];
}): number => {
    let sum = 0;
    for (const li of pricing.lineItems || []) {
        if (li.inclusive) continue;
        sum += parseEuroDe(li.amountEur);
    }
    if (!pricing.bondProductInclusive) {
        sum += parseEuroDe(pricing.bondProductAmountEur);
    }
    for (const sp of pricing.salesProducts || []) {
        if (sp.inclusive) continue;
        sum += parseEuroDe(sp.amountEur);
    }
    if (pricing.hasPflegeGarantie && pricing.garantiePflegesetInGesamtpreis) {
        for (const gp of pricing.garantieProdukte || []) {
            const p = Number(gp?.preisBrutto);
            if (Number.isFinite(p) && p > 0) sum += p;
        }
    }
    return sum > 0 ? Math.ceil(sum - 1e-9) : 0;
};

const sumQuoteTotal = (pricing: {
    lineItems: { id: string; amountEur: string; inclusive?: boolean }[];
    bondProductInclusive: boolean;
    bondProductAmountEur: string;
    salesProducts: { amountEur: string; inclusive?: boolean }[];
    hasPflegeGarantie?: boolean;
    garantiePflegesetInGesamtpreis?: boolean;
    garantieProdukte?: { preisBrutto?: number }[];
}): string => {
    const sum = sumQuoteTotalNumber(pricing);
    return sum > 0 ? formatEuroCeilDe(sum) : '';
};

/** Beträge sind brutto erfasst → Netto / MwSt / Brutto für Rechnungsausweis. */
const splitGrossVat = (grossBrutto: number, vatRate = CONSULT_VAT_RATE) => {
    if (!(grossBrutto > 0)) return { net: 0, vat: 0, gross: 0 };
    const net = grossBrutto / (1 + vatRate / 100);
    const vat = grossBrutto - net;
    return { net, vat, gross: grossBrutto };
};

const emptyTimeInputs = (): Record<string, string> => ({
    beratung_vorbereitung: '',
    farb_blondiertechnik: '',
    pflege_bondbuilder: '',
    schnitt_styling: '',
    heimpflege_material: '',
});

const emptyTimeUnits = (): Record<string, TimeUnit> => ({
    beratung_vorbereitung: 'min',
    farb_blondiertechnik: 'min',
    pflege_bondbuilder: 'min',
    schnitt_styling: 'min',
    heimpflege_material: 'min',
});

const emptyAmountManual = (): Record<string, boolean> => ({
    beratung_vorbereitung: false,
    farb_blondiertechnik: false,
    pflege_bondbuilder: false,
    schnitt_styling: false,
    heimpflege_material: false,
});

const ConsultationQuoteModal: React.FC<ConsultationQuoteModalProps> = ({
    isOpen,
    onClose,
    initialData,
    onArchived,
}) => {
    const clientData = useAppStore((s: any) => s.clientData);
    const { personalData } = usePersonalDataSelector();
    const teamEmployees = (Array.isArray(personalData) ? personalData : [])
        .filter((emp: { id?: string }) => emp?.id)
        .map((emp: { id: string; name?: string }) => ({
            id: String(emp.id),
            name: String(emp.name || '').trim() || 'Ohne Name',
        }));
    const barometerRates = getBarometerHourlyRatesFromAppStore();
    const rate10g = getBarometer10gRate(clientData?.kalkulator || clientData?.clientData?.kalkulator || {});

    const [currentStep, setCurrentStep] = useState(1);
    const [form, setForm] = useState<ConsultationQuoteFormData>(getDefaultConsultationQuoteData);
    const [selectedTeamEmployeeId, setSelectedTeamEmployeeId] = useState('');
    const [timeUnits, setTimeUnits] = useState<Record<string, TimeUnit>>(emptyTimeUnits);
    const [timeInputs, setTimeInputs] = useState<Record<string, string>>(emptyTimeInputs);
    const [amountManual, setAmountManual] = useState<Record<string, boolean>>(emptyAmountManual);
    const [totalManual, setTotalManual] = useState(false);
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const [validationBanner, setValidationBanner] = useState(false);
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>('idle');
    const [archiveErrorDetail, setArchiveErrorDetail] = useState('');
    const [archiveDocId, setArchiveDocId] = useState<string | null>(null);
    const printContainerRef = useRef<HTMLDivElement>(null);

    // iOS: Hintergrund per position:fixed sperren — sonst blockiert der Body den Modal-Scroll / PDF-Flow
    useEffect(() => {
        if (!isOpen) return;
        const scrollY = window.scrollY;
        const prev = {
            overflow: document.body.style.overflow,
            position: document.body.style.position,
            top: document.body.style.top,
            width: document.body.style.width,
            htmlOverflow: document.documentElement.style.overflow,
        };
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        return () => {
            document.documentElement.style.overflow = prev.htmlOverflow;
            document.body.style.overflow = prev.overflow;
            document.body.style.position = prev.position;
            document.body.style.top = prev.top;
            document.body.style.width = prev.width;
            window.scrollTo(0, scrollY);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            setSelectedTeamEmployeeId('');
            setTimeUnits(emptyTimeUnits());
            setTimeInputs(emptyTimeInputs());
            setAmountManual(emptyAmountManual());
            setTotalManual(false);
            setInvalidFields(new Set());
            setValidationBanner(false);
            setDisclaimerAccepted(false);
            setArchiveFeedback('idle');
            setArchiveDocId(null);
            setArchiveErrorDetail('');
            return;
        }

        const salonFromClient = {
            firmenname: clientData?.salonname || clientData?.firmenname || '',
            strasse: clientData?.strasse || '',
            plz: clientData?.plz || '',
            ort: clientData?.ort || '',
        };
        const base = getDefaultConsultationQuoteData(salonFromClient);
        const seeded10g = rate10g > 0 ? formatEuroDe(rate10g) : '';
        base.pricing.materialSurchargePer10g = seeded10g;

        setForm(base);
        setSelectedTeamEmployeeId('');
        setTimeUnits(emptyTimeUnits());
        setTimeInputs(emptyTimeInputs());
        setAmountManual(emptyAmountManual());
        setTotalManual(false);
        setCurrentStep(1);
        setInvalidFields(new Set());
        setValidationBanner(false);
        setDisclaimerAccepted(false);
        setArchiveFeedback('idle');
        setArchiveDocId(initialData?.id || null);
        setArchiveErrorDetail('');

        if (initialData) {
            const payload = initialData.formData || initialData.payload?.formData;
            if (payload && typeof payload === 'object') {
                const nextForm: ConsultationQuoteFormData = {
                    ...base,
                    ...payload,
                    salon: { ...base.salon, ...(payload.salon || {}) },
                    customer: { ...base.customer, ...(payload.customer || {}) },
                    appointment: { ...base.appointment, ...(payload.appointment || {}) },
                    risk: { ...base.risk, ...(payload.risk || {}) },
                    goal: { ...base.goal, ...(payload.goal || {}) },
                    pricing: {
                        ...base.pricing,
                        ...(payload.pricing || {}),
                        materialSurchargePer10g:
                            payload.pricing?.materialSurchargePer10g ??
                            base.pricing.materialSurchargePer10g,
                        bondProductName:
                            payload.pricing?.bondProductName ?? base.pricing.bondProductName,
                        bondProductInclusive:
                            payload.pricing?.bondProductInclusive !== undefined
                                ? Boolean(payload.pricing.bondProductInclusive)
                                : base.pricing.bondProductInclusive,
                        bondProductAmountEur:
                            payload.pricing?.bondProductAmountEur ??
                            base.pricing.bondProductAmountEur,
                        salesProducts: Array.isArray(payload.pricing?.salesProducts)
                            ? payload.pricing.salesProducts.map((p: any) => ({
                                  id: String(p.id || `sale_${Math.random()}`),
                                  label: String(p.label || ''),
                                  amountEur: String(p.amountEur || ''),
                                  inclusive: Boolean(p.inclusive),
                              }))
                            : base.pricing.salesProducts,
                        hasPflegeGarantie: Boolean(
                            payload.pricing?.hasPflegeGarantie ?? base.pricing.hasPflegeGarantie
                        ),
                        garantieWochen: (() => {
                            const w = Number(
                                payload.pricing?.garantieWochen ?? base.pricing.garantieWochen
                            );
                            return Number.isFinite(w) && w > 0 ? Math.round(w) : 4;
                        })(),
                        garantieProdukte: Array.isArray(payload.pricing?.garantieProdukte)
                            ? payload.pricing.garantieProdukte.map((p: any) => ({
                                  id: String(p.id || `gar_${Math.random()}`),
                                  name: String(p.name || ''),
                                  anwendung: String(p.anwendung || ''),
                                  preisBrutto: Number(p.preisBrutto) > 0 ? Number(p.preisBrutto) : 0,
                              }))
                            : base.pricing.garantieProdukte,
                        garantiePflegesetInGesamtpreis: Boolean(
                            payload.pricing?.garantiePflegesetInGesamtpreis ??
                                base.pricing.garantiePflegesetInGesamtpreis
                        ),
                        deposit: {
                            ...CONSULT_DEFAULT_DEPOSIT_CONFIG,
                            ...(payload.pricing?.deposit || {}),
                            anzahlungAktiv: Boolean(
                                payload.pricing?.deposit?.anzahlungAktiv ??
                                    CONSULT_DEFAULT_DEPOSIT_CONFIG.anzahlungAktiv
                            ),
                            anzahlungTyp:
                                payload.pricing?.deposit?.anzahlungTyp === 'festbetrag'
                                    ? 'festbetrag'
                                    : 'prozent',
                            anzahlungWert: Number(
                                payload.pricing?.deposit?.anzahlungWert ??
                                    CONSULT_DEFAULT_DEPOSIT_CONFIG.anzahlungWert
                            ),
                            stornoFristStunden: Number(
                                payload.pricing?.deposit?.stornoFristStunden ??
                                    CONSULT_DEFAULT_DEPOSIT_CONFIG.stornoFristStunden
                            ),
                            ausfallQuoteProzent: Number(
                                payload.pricing?.deposit?.ausfallQuoteProzent ??
                                    CONSULT_DEFAULT_DEPOSIT_CONFIG.ausfallQuoteProzent
                            ),
                        },
                        lineItems:
                            Array.isArray(payload.pricing?.lineItems) && payload.pricing.lineItems.length
                                ? payload.pricing.lineItems
                                : base.pricing.lineItems,
                    },
                    liability: { ...base.liability, ...(payload.liability || {}) },
                    signatures: { ...base.signatures, ...(payload.signatures || {}) },
                };
                setForm(nextForm);
                const stylist = String(nextForm.appointment?.stylistName || '').trim();
                const pool = (Array.isArray(personalData) ? personalData : [])
                    .filter((emp: { id?: string }) => emp?.id)
                    .map((emp: { id: string; name?: string }) => ({
                        id: String(emp.id),
                        name: String(emp.name || '').trim() || 'Ohne Name',
                    }));
                const match = pool.find((e) => e.name === stylist);
                setSelectedTeamEmployeeId(match?.id || '');

                const nextTime = emptyTimeInputs();
                const nextUnits = emptyTimeUnits();
                const nextManual = emptyAmountManual();
                nextForm.pricing.lineItems.forEach((li) => {
                    const id = String(li.id);
                    const te = String(li.timeEffort || '');
                    const minMatch = te.match(/([\d.,]+)\s*Min/i);
                    const stdMatch = te.match(/([\d.,]+)\s*Std/i);
                    if (getRateMode(id) === 'manual') {
                        nextTime[id] = te;
                        nextUnits[id] = 'min';
                    } else if (minMatch) {
                        nextTime[id] = minMatch[1].replace(',', '.');
                        nextUnits[id] = 'min';
                    } else if (stdMatch) {
                        nextTime[id] = stdMatch[1].replace(',', '.');
                        nextUnits[id] = 'std';
                    }
                    if (String(li.amountEur || '').trim()) nextManual[id] = true;
                });
                setTimeInputs(nextTime);
                setTimeUnits(nextUnits);
                setAmountManual(nextManual);
                if (String(nextForm.pricing.totalEur || '').trim()) setTotalManual(true);
            }
            const step = Number(initialData.currentStep);
            if (Number.isFinite(step) && step >= 1 && step <= 2) setCurrentStep(step);
            if (initialData.confirmedDisclaimer) setDisclaimerAccepted(true);
        }
        // Nur bei Open / Archiv-Payload seededn — nicht bei jedem Team-/Barometer-Tick
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, clientData, initialData]);

    const patch = <K extends keyof ConsultationQuoteFormData>(
        key: K,
        value: ConsultationQuoteFormData[K]
    ) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const toggleMulti = <T extends string>(
        list: T[],
        id: T,
        setter: (next: T[]) => void
    ) => {
        setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
    };

    const applyPricingUpdate = (
        nextTime: Record<string, string>,
        nextManual: Record<string, boolean>,
        nextUnits: Record<string, TimeUnit>,
        keepTotalManual: boolean
    ) => {
        setForm((prev) => {
            const lineItems = prev.pricing.lineItems.map((li) => {
                const id = String(li.id);
                const mode = getRateMode(id);
                const unit = nextUnits[id] || 'min';
                const timeEffort =
                    mode === 'manual'
                        ? nextTime[id] || ''
                        : formatTimeEffort(nextTime[id] || '', unit);
                const autoAmount = calcLineAmount(id, nextTime[id] || '', unit, barometerRates);
                const amountEur =
                    nextManual[id] || mode === 'manual' ? li.amountEur : autoAmount;
                return { ...li, timeEffort, amountEur };
            });
            const totalEur = keepTotalManual
                ? prev.pricing.totalEur
                : sumQuoteTotal({ ...prev.pricing, lineItems }) || prev.pricing.totalEur;
            return {
                ...prev,
                pricing: { ...prev.pricing, lineItems, totalEur },
            };
        });
    };

    const handleLineTimeUnitChange = (id: string, unit: TimeUnit) => {
        const nextUnits = { ...timeUnits, [id]: unit };
        setTimeUnits(nextUnits);
        applyPricingUpdate(timeInputs, amountManual, nextUnits, totalManual);
    };

    const handleTimeInputChange = (id: string, raw: string) => {
        const nextTime = { ...timeInputs, [id]: raw };
        setTimeInputs(nextTime);
        applyPricingUpdate(nextTime, amountManual, timeUnits, totalManual);
    };

    const handleAmountChange = (id: string, raw: string) => {
        const nextManual = { ...amountManual, [id]: true };
        setAmountManual(nextManual);
        setForm((prev) => {
            const lineItems = prev.pricing.lineItems.map((li) =>
                li.id === id ? { ...li, amountEur: raw } : li
            );
            const totalEur = totalManual
                ? prev.pricing.totalEur
                : sumQuoteTotal({ ...prev.pricing, lineItems }) || prev.pricing.totalEur;
            return { ...prev, pricing: { ...prev.pricing, lineItems, totalEur } };
        });
    };

    const handleLabelChange = (id: string, label: string) => {
        setForm((prev) => ({
            ...prev,
            pricing: {
                ...prev.pricing,
                lineItems: prev.pricing.lineItems.map((li) =>
                    li.id === id ? { ...li, label } : li
                ),
            },
        }));
    };

    const handleRecalcLine = (id: string) => {
        const nextManual = { ...amountManual, [id]: false };
        setAmountManual(nextManual);
        applyPricingUpdate(timeInputs, nextManual, timeUnits, totalManual);
    };

    const handleAddCustomLine = () => {
        const id = `custom_${Date.now()}`;
        setTimeInputs((prev) => ({ ...prev, [id]: '' }));
        setTimeUnits((prev) => ({ ...prev, [id]: 'min' }));
        setAmountManual((prev) => ({ ...prev, [id]: false }));
        setForm((prev) => {
            const items = [...prev.pricing.lineItems];
            const homeIdx = items.findIndex((li) => li.id === 'heimpflege_material');
            const insertAt = homeIdx >= 0 ? homeIdx : items.length;
            items.splice(insertAt, 0, {
                id,
                label: 'Weitere Leistung',
                timeEffort: '',
                amountEur: '',
            });
            return {
                ...prev,
                pricing: { ...prev.pricing, lineItems: items },
            };
        });
    };

    const handleRemoveCustomLine = (id: string) => {
        if (isStandardLineId(id)) return;
        setForm((prev) => {
            const lineItems = prev.pricing.lineItems.filter((li) => li.id !== id);
            const totalEur = totalManual
                ? prev.pricing.totalEur
                : sumQuoteTotal({ ...prev.pricing, lineItems }) || prev.pricing.totalEur;
            return { ...prev, pricing: { ...prev.pricing, lineItems, totalEur } };
        });
        setTimeInputs((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setTimeUnits((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setAmountManual((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const addSalesProduct = () => {
        setForm((prev) => ({
            ...prev,
            pricing: {
                ...prev.pricing,
                salesProducts: [
                    ...(prev.pricing.salesProducts || []),
                    {
                        id: `sale_${Date.now()}`,
                        label: '',
                        amountEur: '',
                        inclusive: false,
                    },
                ],
            },
        }));
    };

    const updateSalesProduct = (
        productId: string,
        patchLine: Partial<{ label: string; amountEur: string; inclusive: boolean }>
    ) => {
        setForm((prev) => {
            const salesProducts = (prev.pricing.salesProducts || []).map((p) =>
                p.id === productId ? { ...p, ...patchLine } : p
            );
            const nextPricing = { ...prev.pricing, salesProducts };
            const totalEur = totalManual
                ? prev.pricing.totalEur
                : sumQuoteTotal(nextPricing) || prev.pricing.totalEur;
            return { ...prev, pricing: { ...nextPricing, totalEur } };
        });
    };

    const removeSalesProduct = (productId: string) => {
        setForm((prev) => {
            const salesProducts = (prev.pricing.salesProducts || []).filter(
                (p) => p.id !== productId
            );
            const nextPricing = { ...prev.pricing, salesProducts };
            const totalEur = totalManual
                ? prev.pricing.totalEur
                : sumQuoteTotal(nextPricing) || prev.pricing.totalEur;
            return { ...prev, pricing: { ...nextPricing, totalEur } };
        });
    };

    const patchPflegeGarantie = (
        patchGar: Partial<{
            hasPflegeGarantie: boolean;
            garantieWochen: number;
            garantieProdukte: ConsultGarantieProdukt[];
            garantiePflegesetInGesamtpreis: boolean;
        }>
    ) => {
        setForm((prev) => {
            const nextPricing = { ...prev.pricing, ...patchGar };
            const totalEur = totalManual
                ? prev.pricing.totalEur
                : sumQuoteTotal(nextPricing) || prev.pricing.totalEur;
            return { ...prev, pricing: { ...nextPricing, totalEur } };
        });
    };

    const addGarantieProdukt = () => {
        patchPflegeGarantie({
            garantieProdukte: [
                ...(form.pricing.garantieProdukte || []),
                {
                    id: `gar_${Date.now()}`,
                    name: '',
                    anwendung: '',
                    preisBrutto: 0,
                },
            ],
        });
    };

    const updateGarantieProdukt = (
        productId: string,
        patchLine: Partial<ConsultGarantieProdukt>
    ) => {
        const garantieProdukte = (form.pricing.garantieProdukte || []).map((p) =>
            p.id === productId ? { ...p, ...patchLine } : p
        );
        patchPflegeGarantie({ garantieProdukte });
    };

    const removeGarantieProdukt = (productId: string) => {
        patchPflegeGarantie({
            garantieProdukte: (form.pricing.garantieProdukte || []).filter(
                (p) => p.id !== productId
            ),
        });
    };

    const patchBondField = (
        patchBond: Partial<{
            bondProductName: string;
            bondProductInclusive: boolean;
            bondProductAmountEur: string;
        }>
    ) => {
        setForm((prev) => {
            const nextPricing = { ...prev.pricing, ...patchBond };
            if (patchBond.bondProductInclusive === true) {
                nextPricing.bondProductAmountEur = '';
            }
            const totalEur = totalManual
                ? prev.pricing.totalEur
                : sumQuoteTotal(nextPricing) || prev.pricing.totalEur;
            return { ...prev, pricing: { ...nextPricing, totalEur } };
        });
    };

    const patchDeposit = (patchDep: Partial<PeConsultationDepositConfig>) => {
        setForm((prev) => ({
            ...prev,
            pricing: {
                ...prev.pricing,
                deposit: {
                    ...(prev.pricing.deposit ?? CONSULT_DEFAULT_DEPOSIT_CONFIG),
                    ...patchDep,
                },
            },
        }));
    };

    const setLineInclusive = (id: string, inclusive: boolean) => {
        setForm((prev) => {
            const lineItems = prev.pricing.lineItems.map((li) =>
                li.id === id
                    ? {
                          ...li,
                          inclusive,
                          amountEur: inclusive ? '' : li.amountEur,
                      }
                    : li
            );
            const nextPricing = { ...prev.pricing, lineItems };
            const totalEur = totalManual
                ? prev.pricing.totalEur
                : sumQuoteTotal(nextPricing) || prev.pricing.totalEur;
            return { ...prev, pricing: { ...nextPricing, totalEur } };
        });
    };

    const handleTotalChange = (raw: string) => {
        setTotalManual(true);
        clearErr('pricing.totalEur');
        patch('pricing', { ...form.pricing, totalEur: raw });
    };

    const handleRecalcTotal = () => {
        setTotalManual(false);
        setForm((prev) => ({
            ...prev,
            pricing: {
                ...prev.pricing,
                totalEur: sumQuoteTotal(prev.pricing),
            },
        }));
    };

    const validateStep1 = (): string[] => {
        const err: string[] = [];
        if (isEmpty(form.salon.firmenname)) err.push('salon.firmenname');
        if (isEmpty(form.customer.vorname) && isEmpty(form.customer.nachname)) {
            err.push('customer.vorname');
            err.push('customer.nachname');
        }
        if (isEmpty(form.appointment.dateIso)) err.push('appointment.dateIso');
        if (isEmpty(form.appointment.stylistName)) err.push('appointment.stylistName');
        if (!form.risk.hairCondition) err.push('risk.hairCondition');
        if (!form.risk.strandTest) err.push('risk.strandTest');
        if (isEmpty(form.goal.customerWish)) err.push('goal.customerWish');
        if (!form.goal.feasibility) err.push('goal.feasibility');
        if (form.goal.feasibility === 'mehrstufenprozess' && isEmpty(form.goal.plannedSessions)) {
            err.push('goal.plannedSessions');
        }
        if (isEmpty(form.pricing.totalEur)) err.push('pricing.totalEur');
        return err;
    };

    const fieldClass = (id: string) =>
        `${INPUT_CLASS} ${invalidFields.has(id) ? ERROR_RING : ''}`;

    const fieldHint = (id: string, text: string) =>
        invalidFields.has(id) ? (
            <p className="mt-1 text-[10px] text-red-400">{text}</p>
        ) : null;

    const clearErr = (id: string) => {
        setInvalidFields((prev) => {
            if (!prev.has(id)) return prev;
            const n = new Set(prev);
            n.delete(id);
            if (n.size === 0) setValidationBanner(false);
            return n;
        });
    };

    const handleWeiter = () => {
        const errors = validateStep1();
        if (errors.length) {
            setInvalidFields(new Set(errors));
            setValidationBanner(true);
            requestAnimationFrame(() => {
                const el = document.querySelector(
                    `[data-cq-field="${errors[0]}"]`
                ) as HTMLElement | null;
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
            return;
        }
        setInvalidFields(new Set());
        setValidationBanner(false);
        setCurrentStep(2);
    };

    const handleClose = () => {
        setCurrentStep(1);
        setDisclaimerAccepted(false);
        setArchiveFeedback('idle');
        onClose();
    };

    const isActionLocked = !disclaimerAccepted;
    const btnClass = (base: string) => `${base} ${isActionLocked ? 'opacity-50 cursor-not-allowed' : ''}`;

    const salonAddress = [
        form.salon.strasse,
        [form.salon.plz, form.salon.ort].filter(Boolean).join(' '),
    ]
        .filter(Boolean)
        .join(', ');

    const handlePrint = () => {
        if (isActionLocked) return;
        const iframe = document.createElement('iframe');
        // Messbar (nicht 0×0), sonst stimmen scrollHeight/Fit nicht
        iframe.style.cssText =
            'position:fixed;left:-12000px;top:0;width:210mm;height:297mm;border:none;visibility:hidden;';
        document.body.appendChild(iframe);
        const win = iframe.contentWindow;
        if (!win) {
            document.body.removeChild(iframe);
            return;
        }
        const html =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-consultation-quote-print')?.innerHTML ||
            '';
        win.document.open();
        win.document.write(`<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"/><title>${CONSULT_QUOTE_TITLE}</title>
<style>
${CQ_PRINT_CSS}
</style></head><body>${html}</body></html>`);
        win.document.close();

        const runPrint = () => {
            try {
                const doc = win.document;
                // A4 297mm − 2×10mm Rand ≈ druckbarer Inhalt (wie PDF fitSectionsToPage)
                const mmToPx = 96 / 25.4;
                const printableHpx = (297 - 20) * mmToPx;
                const pages = Array.from(
                    doc.querySelectorAll<HTMLElement>('.cq-a4-page, .cq-a4-page-2')
                );
                pages.forEach((page) => {
                    const h = Math.max(page.scrollHeight, page.offsetHeight, 1);
                    if (h <= printableHpx * 1.02) return;
                    // Gleiche Logik wie PDF: Seite proportional auf 1 A4 skalieren
                    const fit = Math.max(0.72, Math.min(1, (printableHpx / h) * 0.98));
                    page.style.zoom = String(fit);
                });
            } catch (err) {
                console.warn('ConsultationQuote Print-Fit fehlgeschlagen:', err);
            }
            win.focus();
            win.print();
            setTimeout(() => {
                if (iframe.parentNode) document.body.removeChild(iframe);
            }, 1000);
        };

        // Layout + Fonts brauchen einen Tick, sonst falsch gemessen
        setTimeout(runPrint, 450);
    };

    const handleDownloadPdf = async () => {
        if (isActionLocked || isPdfDownloading) return;
        const html =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-consultation-quote-print')?.innerHTML ||
            '';
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            const dateIso = form.appointment.dateIso || new Date().toISOString().slice(0, 10);
            await downloadHtmlAsPdf({
                html,
                filename: `Beratungsprotokoll_KV_${dateIso}.pdf`,
                extraCss: CQ_PDF_CSS,
                splitPrintBreaks: true,
                scrubMode: 'minimal',
                fitSectionsToPage: true,
                letterheadOnBreaks: false,
            });
        } catch (err: any) {
            console.error('ConsultationQuote PDF fehlgeschlagen:', err);
            window.alert(
                'PDF konnte nicht heruntergeladen werden: ' +
                    (String(err?.message || '').match(/setEnd|Range|offset/i)
                        ? 'PDF-Erstellung ist fehlgeschlagen. Bitte Seite neu laden und erneut versuchen.'
                        : err?.message || 'Unbekannter Fehler')
            );
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleArchive = async (asDraft = false) => {
        if (isSaving) return;
        if (!asDraft && isActionLocked) return;
        setIsSaving(true);
        setArchiveFeedback('idle');
        setArchiveErrorDetail('');
        const htmlContent =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-consultation-quote-print')?.innerHTML ||
            '';
        try {
            // Firestore lehnt undefined/NaN ab — Payload hart bereinigen.
            const depositRaw = form.pricing.deposit ?? CONSULT_DEFAULT_DEPOSIT_CONFIG;
            const depositClean: PeConsultationDepositConfig = {
                anzahlungAktiv: Boolean(depositRaw.anzahlungAktiv),
                anzahlungTyp:
                    depositRaw.anzahlungTyp === 'festbetrag' ? 'festbetrag' : 'prozent',
                anzahlungWert: Number(depositRaw.anzahlungWert) || 0,
                stornoFristStunden: Number(depositRaw.stornoFristStunden) || 48,
                ausfallQuoteProzent: Number(depositRaw.ausfallQuoteProzent) || 60,
            };
            if (depositRaw.zahlungsart) depositClean.zahlungsart = depositRaw.zahlungsart;
            if (depositRaw.bezahltAm?.trim()) depositClean.bezahltAm = depositRaw.bezahltAm.trim();

            const formDataForArchive = JSON.parse(
                JSON.stringify({
                    ...form,
                    pricing: { ...form.pricing, deposit: depositClean },
                })
            ) as ConsultationQuoteFormData;

            // Firestore-Hardcap ~1 MiB — HTML ggf. weglassen, Formular bleibt speicherbar.
            const htmlSafe =
                htmlContent && htmlContent.length < 850_000 ? htmlContent : '';

            const id = await savePePapersArchiveDoc({
                id: archiveDocId,
                documentType: 'consultation-quote',
                status: asDraft ? 'entwurf' : 'final',
                salonName: form.salon.firmenname || 'Unbekannter Salon',
                mitarbeiterName: `${form.customer.vorname} ${form.customer.nachname}`.trim(),
                confirmedDisclaimer: asDraft ? disclaimerAccepted : true,
                currentStep,
                payload: {
                    formData: formDataForArchive,
                    documentId: CONSULT_QUOTE_DOC_ID,
                    generatedHtml: htmlSafe,
                },
            });
            setArchiveDocId(id);
            setArchiveFeedback(asDraft ? 'draft' : 'success');
            onArchived?.();
            if (asDraft) setTimeout(() => onClose(), 600);
            else setTimeout(() => setArchiveFeedback('idle'), 3000);
        } catch (e) {
            console.error('ConsultationQuote Archiv fehlgeschlagen:', e);
            const msg =
                e instanceof Error
                    ? e.message
                    : typeof e === 'string'
                      ? e
                      : 'Unbekannter Fehler';
            setArchiveErrorDetail(msg);
            setArchiveFeedback('error');
        } finally {
            setIsSaving(false);
        }
    };

    const grossTotalNum =
        parseEuroDe(form.pricing.totalEur) || sumQuoteTotalNumber(form.pricing);
    const vatSplit = splitGrossVat(grossTotalNum);
    const depositCfg: PeConsultationDepositConfig =
        form.pricing.deposit ?? CONSULT_DEFAULT_DEPOSIT_CONFIG;
    const depositAmountEur = calcConsultDepositAmountEur(grossTotalNum, depositCfg);
    const customerName =
        `${form.customer.vorname} ${form.customer.nachname}`.trim() || '____________';

    const renderDocument = () => (
        <div
            ref={printContainerRef}
            id="pepapers-consultation-quote-print"
            className="w-full h-auto overflow-visible"
        >
            <style>{CQ_DOC_CSS}</style>

            {/* SEITE 1 */}
            <div className={A4_PAGE_CLASS}>
                <p className="cq-screen-only cq-page-label">Seite 1 von 2</p>

                <table className="cq-header">
                    <tbody>
                        <tr>
                            <td>
                                <h1 className="cq-title">
                                    <span className="cq-title-pe">PE</span>
                                    <span className="cq-title-rest">Papers</span>
                                    <span className="cq-title-sub">Beratungsprotokoll</span>
                                </h1>
                                {form.salon.firmenname ? (
                                    <p className="cq-salon-line">{form.salon.firmenname}</p>
                                ) : null}
                            </td>
                            <td className="cq-header-meta">
                                <p>
                                    Dokument: <strong>{CONSULT_QUOTE_DOC_ID}</strong>
                                </p>
                                <p>
                                    Datum:{' '}
                                    <strong>{formatGermanDate(form.appointment.dateIso)}</strong>
                                </p>
                                <p>
                                    Stylist/in:{' '}
                                    <strong>{form.appointment.stylistName || '—'}</strong>
                                </p>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <p className="cq-lead">{CONSULT_QUOTE_SUBTITLE}</p>

                <div className="cq-box">
                    <h3 className="cq-h">1. Stammdaten &amp; Termin</h3>
                    <p className="cq-row">
                        <strong>Salon:</strong> {form.salon.firmenname || '____________'}
                        <br />
                        <strong>Anschrift:</strong> {salonAddress || '____________'}
                    </p>
                    <p className="cq-row">
                        <strong>Kunde/Kundin:</strong> {customerName}
                        <br />
                        <strong>Telefon:</strong> {form.customer.telefon || '—'} ·{' '}
                        <strong>E-Mail:</strong> {form.customer.email || '—'}
                    </p>
                    <p className="cq-row">
                        <strong>Datum:</strong> {formatGermanDate(form.appointment.dateIso)}
                        <br />
                        <strong>Ausführende/r Stylist/in:</strong>{' '}
                        {form.appointment.stylistName || '____________'}
                    </p>
                </div>

                <div className="cq-box">
                    <h3 className="cq-h">2. Haaranalyse &amp; chemische Vorgeschichte</h3>
                    <p className="cq-row">
                        <strong>Ausgangsbasis:</strong>
                    </p>
                    {CONSULT_HAIR_BASE_OPTIONS.map((o) => (
                        <p key={o.id} className="cq-check">
                            {mark(form.risk.hairBase.includes(o.id))} {o.label}
                        </p>
                    ))}
                    <p className="cq-row" style={{ marginTop: 10 }}>
                        <strong>Frühere Behandlungen (letzte 12 Monate):</strong>
                    </p>
                    {CONSULT_PRIOR_OPTIONS.map((o) => (
                        <p key={o.id} className="cq-check">
                            {mark(form.risk.priorTreatments.includes(o.id))} {o.label}
                        </p>
                    ))}
                    {form.risk.customPriorTreatments?.trim() ? (
                        <p className="cq-row">
                            <strong>Sonstige Vorbehandlungen:</strong>{' '}
                            {form.risk.customPriorTreatments.trim()}
                        </p>
                    ) : null}
                    <p className="cq-row" style={{ marginTop: 10 }}>
                        <strong>Haarzustand:</strong>
                    </p>
                    {CONSULT_CONDITION_OPTIONS.map((o) => (
                        <p key={o.id} className="cq-check">
                            {mark(form.risk.hairCondition === o.id)} {o.label}
                        </p>
                    ))}
                    {form.risk.customConditionNotes?.trim() ? (
                        <p className="cq-row">
                            <strong>Besonderheiten Haar/Kopfhaut:</strong>{' '}
                            {form.risk.customConditionNotes.trim()}
                        </p>
                    ) : null}
                    <p className="cq-row" style={{ marginTop: 10 }}>
                        <strong>Teststrähne durchgeführt?:</strong>{' '}
                        {mark(form.risk.strandTest === 'ja')} Ja &nbsp;{' '}
                        {mark(form.risk.strandTest === 'nein_abgelehnt')} Nein (nach Aufklärung
                        abgelehnt)
                    </p>
                    <p className="cq-row">
                        <strong>Ergebnis der Teststrähne:</strong>{' '}
                        {form.risk.strandTestResult || '—'}
                    </p>
                </div>

                <div className="cq-box">
                    <h3 className="cq-h">3. Zieldefinition &amp; Machbarkeit</h3>
                    <p className="cq-row">
                        <strong>Kundenwunsch:</strong> {form.goal.customerWish || '____________'}
                    </p>
                    <p className="cq-row">
                        <strong>Fachliche Einschätzung:</strong>
                    </p>
                    <p className="cq-check">
                        {mark(form.goal.feasibility === 'eine_sitzung')} In einer Sitzung
                        realisierbar
                    </p>
                    <p className="cq-check">
                        {mark(form.goal.feasibility === 'mehrstufenprozess')} Zwischenschritt /
                        Mehrstufenprozess erforderlich
                        {form.goal.feasibility === 'mehrstufenprozess'
                            ? ` (geplante Sitzungen: ${form.goal.plannedSessions || '___'})`
                            : ''}
                    </p>
                    <p className="cq-legal" style={{ marginTop: 10 }}>
                        <strong>Grenzen der Machbarkeit:</strong> {CONSULT_FEASIBILITY_HINT}
                    </p>
                </div>
            </div>

            {/* SEITE 2 */}
            <div className={`print-break pepapers-print-break cq-a4-page-2 ${A4_PAGE_CLASS}`}>
                <p className="cq-screen-only cq-page-label">Seite 2 von 2</p>

                <div className="cq-box">
                    <h3 className="cq-h">4. Kostenvoranschlag &amp; Zeitaufwand</h3>
                    <div className="cq-table-wrap">
                        <table className="cq-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '8%' }}>Pos.</th>
                                    <th style={{ width: '46%' }}>Position</th>
                                    <th style={{ width: '22%' }}>Zeitaufwand</th>
                                    <th style={{ width: '24%' }} className="cq-num">
                                        Betrag brutto
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {form.pricing.lineItems.map((item, idx) => (
                                    <tr key={item.id}>
                                        <td>{idx + 1}.</td>
                                        <td>
                                            {item.label}
                                            {item.id === 'pflege_bondbuilder' &&
                                            form.pricing.bondProductName?.trim() ? (
                                                <span className="cq-muted">
                                                    Angewendet:{' '}
                                                    {form.pricing.bondProductName.trim()}
                                                    {form.pricing.bondProductInclusive
                                                        ? ' — inkl. (ohne Aufpreis)'
                                                        : ' — Extra'}
                                                </span>
                                            ) : null}
                                            {item.inclusive ? (
                                                <span className="cq-muted">inkl. (ohne Aufpreis)</span>
                                            ) : null}
                                        </td>
                                        <td>{item.inclusive ? '—' : item.timeEffort || '—'}</td>
                                        <td className="cq-num">
                                            {item.inclusive
                                                ? 'inkl.'
                                                : item.amountEur
                                                  ? `${item.amountEur} EUR`
                                                  : '—'}
                                        </td>
                                    </tr>
                                ))}
                                {!form.pricing.bondProductInclusive &&
                                (form.pricing.bondProductName?.trim() ||
                                    form.pricing.bondProductAmountEur?.trim()) ? (
                                    <tr>
                                        <td>{form.pricing.lineItems.length + 1}.</td>
                                        <td>
                                            {form.pricing.bondProductName?.trim() || 'Bond-Produkt'}
                                            <span className="cq-muted">Extra (berechnet)</span>
                                        </td>
                                        <td>—</td>
                                        <td className="cq-num">
                                            {form.pricing.bondProductAmountEur
                                                ? `${form.pricing.bondProductAmountEur} EUR`
                                                : '—'}
                                        </td>
                                    </tr>
                                ) : null}
                                {(form.pricing.salesProducts || [])
                                    .filter((sp) => sp.label.trim() || sp.amountEur.trim())
                                    .map((sp, sIdx) => {
                                        const bondExtra =
                                            !form.pricing.bondProductInclusive &&
                                            (form.pricing.bondProductName?.trim() ||
                                                form.pricing.bondProductAmountEur?.trim())
                                                ? 1
                                                : 0;
                                        return (
                                            <tr key={`sale-${sp.id}`}>
                                                <td>
                                                    {form.pricing.lineItems.length +
                                                        bondExtra +
                                                        sIdx +
                                                        1}
                                                    .
                                                </td>
                                                <td>
                                                    {sp.label || 'Verkaufsprodukt / Mitnahme'}
                                                    <span className="cq-muted">
                                                        {sp.inclusive
                                                            ? 'inkl. (ohne Aufpreis)'
                                                            : 'Verkaufsprodukt'}
                                                    </span>
                                                </td>
                                                <td>—</td>
                                                <td className="cq-num">
                                                    {sp.inclusive
                                                        ? 'inkl.'
                                                        : sp.amountEur
                                                          ? `${sp.amountEur} EUR`
                                                          : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>

                    <div className="cq-totals">
                        <div className="cq-totals-row">
                            <span>Nettobetrag</span>
                            <span>
                                {vatSplit.gross > 0
                                    ? `${formatEuroDe(vatSplit.net)} EUR`
                                    : '— EUR'}
                            </span>
                        </div>
                        <div className="cq-totals-row">
                            <span>zzgl. MwSt. {CONSULT_VAT_RATE} %</span>
                            <span>
                                {vatSplit.gross > 0
                                    ? `${formatEuroDe(vatSplit.vat)} EUR`
                                    : '— EUR'}
                            </span>
                        </div>
                        <div className="cq-totals-row cq-totals-gross">
                            <span>Gesamtbetrag (Brutto)</span>
                            <span>
                                {vatSplit.gross > 0
                                    ? `${formatEuroCeilDe(vatSplit.gross)} EUR`
                                    : form.pricing.totalEur
                                      ? `${form.pricing.totalEur} EUR`
                                      : '— EUR'}
                            </span>
                        </div>
                    </div>

                    <p className="cq-legal">
                        <strong>Kostenvoranschlag &amp; Abrechnung nach tatsächlichem Aufwand:</strong>{' '}
                        {CONSULT_BUFFER_CLAUSE.replace(
                            /^Kostenvoranschlag & Abrechnung nach tatsächlichem Aufwand:\s*/,
                            ''
                        )}
                    </p>
                    {form.pricing.materialSurchargePer10g?.trim() ? (
                        <p className="cq-legal">
                            <strong>Material-Mehrverbrauch:</strong>{' '}
                            {buildConsultMaterialSurchargeClause(
                                form.pricing.materialSurchargePer10g
                            ).replace(/^Material-Mehrverbrauch:\s*/, '')}
                        </p>
                    ) : null}
                </div>

                {depositCfg.anzahlungAktiv ? (
                    <div className="cq-signal-box cq-signal-accent">
                        <h3 className="cq-signal-h">Verbindliche Termin-Anzahlung</h3>
                        <p className="cq-signal-amt">
                            Sofort fällig:{' '}
                            <strong>{formatEuroDe(depositAmountEur)} EUR</strong>
                            {depositCfg.anzahlungTyp === 'prozent' ? (
                                <>
                                    {' '}
                                    (<strong>{depositCfg.anzahlungWert} %</strong> vom Gesamtbetrag)
                                </>
                            ) : (
                                <>
                                    {' '}
                                    (<strong>Festbetrag</strong>)
                                </>
                            )}
                        </p>
                        <p className="cq-signal-note">
                            <strong>Wird zu 100 % mit dem Endbetrag verrechnet.</strong> Der Termin wird
                            erst mit Eingang dieser Anzahlung verbindlich reserviert — ohne Anzahlung
                            besteht kein Anspruch auf das Zeitfenster.
                        </p>
                    </div>
                ) : null}

                <div className="cq-box">
                    <h3 className="cq-h">5. Haftungsausschluss &amp; Pflegeverpflichtung</h3>
                    <p className="cq-legal">
                        <strong>Risikobelehrung:</strong> {CONSULT_RISK_NOTICE}
                    </p>
                    <p className="cq-legal">
                        <strong>Heimpflege:</strong> {CONSULT_HOMECARE_CLAUSE}
                    </p>
                </div>

                {form.pricing.hasPflegeGarantie ? (
                    <div className="cq-box">
                        <h3 className="cq-h">
                            Freiwillige Haltbarkeits- &amp; Farbfrische-Garantie (
                            {Number.isFinite(form.pricing.garantieWochen) &&
                            form.pricing.garantieWochen > 0
                                ? form.pricing.garantieWochen
                                : 4}{' '}
                            Wochen)
                        </h3>
                        <p className="cq-legal">
                            <strong>Pflege-Vereinbarung:</strong> {CONSULT_PFLEGE_GARANTIE_CLAUSE}
                        </p>
                        <div className="cq-table-wrap">
                            <table className="cq-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '8%' }}>Pos.</th>
                                        <th style={{ width: '34%' }}>Produkt</th>
                                        <th style={{ width: '38%' }}>Anwendung / Routine</th>
                                        <th style={{ width: '20%' }} className="cq-num">
                                            Preis brutto
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(form.pricing.garantieProdukte || []).filter(
                                        (gp) => gp.name.trim() || gp.anwendung.trim() || gp.preisBrutto > 0
                                    ).length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="cq-muted">
                                                Keine Pflegeprodukte hinterlegt.
                                            </td>
                                        </tr>
                                    ) : (
                                        (form.pricing.garantieProdukte || [])
                                            .filter(
                                                (gp) =>
                                                    gp.name.trim() ||
                                                    gp.anwendung.trim() ||
                                                    gp.preisBrutto > 0
                                            )
                                            .map((gp, gIdx) => (
                                                <tr key={gp.id}>
                                                    <td>{gIdx + 1}.</td>
                                                    <td>{gp.name.trim() || '—'}</td>
                                                    <td>{gp.anwendung.trim() || '—'}</td>
                                                    <td className="cq-num">
                                                        {gp.preisBrutto > 0
                                                            ? `${formatEuroDe(gp.preisBrutto)} EUR`
                                                            : '—'}
                                                    </td>
                                                </tr>
                                            ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {form.pricing.garantiePflegesetInGesamtpreis ? (
                            <p className="cq-legal">
                                <strong>Hinweis:</strong> Die aufgeführten Pflegeprodukte sind als
                                Pflegeset in den kalkulierten Gesamtbetrag eingerechnet.
                            </p>
                        ) : null}
                        <p className="cq-legal" style={{ marginTop: 8 }}>
                            {CONSULT_PFLEGE_GARANTIE_ACK}
                        </p>
                        <div className="cq-sig-grid" style={{ marginTop: 10 }}>
                            <div className="cq-sig-col">
                                <p className="cq-row">Bestätigung Kunde/Kundin (Pflegeroutine)</p>
                                <div className="cq-sig-line" />
                            </div>
                            <div className="cq-sig-col">
                                <p className="cq-row">
                                    Ort / Datum: {form.signatures.place || '____________'} /{' '}
                                    {formatGermanDate(form.signatures.dateIso)}
                                </p>
                                <div className="cq-sig-line" />
                            </div>
                        </div>
                    </div>
                ) : null}

                <div className="cq-signal-box">
                    <h3 className="cq-signal-h">
                        Vereinbarung zur Terminverbindlichkeit (§ 615 BGB)
                    </h3>
                    <div className="cq-clause-list">
                        <p>
                            <strong>1. Kostenfreie Stornierung:</strong> Bis mindestens{' '}
                            {depositCfg.stornoFristStunden} Stunden vor Beginn.
                        </p>
                        <p>
                            <strong>2. Härtefallklausel (Akute Erkrankung):</strong> Bei unverschuldeter,
                            plötzlicher Erkrankung (ärztliches Attest binnen 3 Werktagen) wird der Termin
                            kostenfrei verlegt; eine Anzahlung bleibt als Gutschrift bestehen.
                        </p>
                        <p>
                            <strong>3. Kurzfristige Absage / No-Show:</strong> Bei unbegründeter Absage
                            unterhalb der Frist oder Nichterscheinen wird eine Ausfallentschädigung von{' '}
                            <strong>{depositCfg.ausfallQuoteProzent} %</strong> des kalkulierten
                            Dienstleistungswertes (abzgl. ersparter Materialaufwendungen) fällig. Eine
                            Anzahlung wird angerechnet.
                        </p>
                        <p>
                            <strong>4. Gesetzliche Öffnungsklausel:</strong> Dem Kunden bleibt der Nachweis
                            gestattet, dass kein oder ein wesentlich geringerer Schaden entstanden ist.
                        </p>
                    </div>
                </div>

                <div className="cq-box">
                    <h3 className="cq-h">6. Verbindliche Auftragserteilung</h3>
                    <p className="cq-legal">{CONSULT_ORDER_CONFIRMATION}</p>
                    <p className="cq-row">
                        <strong>Ort:</strong> {form.signatures.place || '____________'}
                        &nbsp;&nbsp;
                        <strong>Datum:</strong> {formatGermanDate(form.signatures.dateIso)}
                    </p>
                    <div className="cq-sig-grid">
                        <div className="cq-sig-col">
                            <p className="cq-row">Unterschrift Kunde/Kundin</p>
                            <div className="cq-sig-line" />
                        </div>
                        <div className="cq-sig-col">
                            <p className="cq-row">Unterschrift Stylist/in</p>
                            <div className="cq-sig-line" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (!isOpen) return null;

    const modalWidthClass = currentStep === 2 ? 'max-w-5xl' : 'max-w-2xl';

    return createPortal(
        <>
            <div
                className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md print:hidden"
                onClick={handleClose}
                aria-hidden="true"
            />
            <div className="fixed inset-x-0 top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-start justify-center overflow-hidden p-4 pb-8 font-sans print:inset-0 print:top-0 print:overflow-visible print:p-0">
                <div
                    className={`bg-pe-card dark:bg-[#111] rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full ${modalWidthClass} max-h-[calc(100vh-9.5rem)] md:max-h-[calc(100vh-10rem)] flex min-h-0 flex-col justify-between overflow-hidden relative transition-all duration-200 my-2 print:shadow-none print:border-0 print:max-w-none print:max-h-none print:rounded-none print:my-0 print:bg-white`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white bg-pe-elevated dark:bg-[#1a1a1a] rounded-lg p-2 border border-pe-border-strong dark:border-[#333] z-[60] print:hidden"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="bg-pe-elevated dark:bg-[#111622] shrink-0 print:hidden border-b border-pe-border dark:border-[#222]">
                        <div className="px-6 pt-10 pb-4">
                            <h3 className="text-lg font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest pr-8 leading-snug">
                                High-Ticket Beratung &amp; Kostenvoranschlag
                            </h3>
                            <p className="text-center text-xs text-pe-muted dark:text-slate-400 uppercase tracking-widest mt-2">
                                Schritt {currentStep} von 2: {STEP_LABELS[currentStep]}
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar print:p-0 print:overflow-visible">
                        {currentStep === 1 && (
                            <div className="space-y-5 pb-4">
                                <section className="space-y-3">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2">
                                        Salon &amp; Kunde
                                    </p>
                                    <div>
                                        <label className={LABEL_CLASS}>Salon / Firma</label>
                                        <input
                                            data-cq-field="salon.firmenname"
                                            className={fieldClass('salon.firmenname')}
                                            value={form.salon.firmenname}
                                            onChange={(e) => {
                                                clearErr('salon.firmenname');
                                                patch('salon', { ...form.salon, firmenname: e.target.value });
                                            }}
                                        />
                                        {fieldHint('salon.firmenname', 'Bitte Salon / Firma angeben')}
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        <div className="sm:col-span-2">
                                            <label className={LABEL_CLASS}>Straße</label>
                                            <input
                                                className={INPUT_CLASS}
                                                value={form.salon.strasse}
                                                onChange={(e) =>
                                                    patch('salon', { ...form.salon, strasse: e.target.value })
                                                }
                                            />
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>PLZ</label>
                                            <input
                                                className={INPUT_CLASS}
                                                value={form.salon.plz}
                                                onChange={(e) =>
                                                    patch('salon', { ...form.salon, plz: e.target.value })
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Ort</label>
                                        <input
                                            className={INPUT_CLASS}
                                            value={form.salon.ort}
                                            onChange={(e) => {
                                                patch('salon', { ...form.salon, ort: e.target.value });
                                                if (!form.signatures.place) {
                                                    patch('signatures', {
                                                        ...form.signatures,
                                                        place: e.target.value,
                                                    });
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className={LABEL_CLASS}>Vorname Kunde</label>
                                            <input
                                                data-cq-field="customer.vorname"
                                                className={fieldClass('customer.vorname')}
                                                value={form.customer.vorname}
                                                onChange={(e) => {
                                                    clearErr('customer.vorname');
                                                    clearErr('customer.nachname');
                                                    patch('customer', {
                                                        ...form.customer,
                                                        vorname: e.target.value,
                                                    });
                                                }}
                                            />
                                            {fieldHint('customer.vorname', 'Bitte Vor- oder Nachname angeben')}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Nachname Kunde</label>
                                            <input
                                                data-cq-field="customer.nachname"
                                                className={fieldClass('customer.nachname')}
                                                value={form.customer.nachname}
                                                onChange={(e) => {
                                                    clearErr('customer.vorname');
                                                    clearErr('customer.nachname');
                                                    patch('customer', {
                                                        ...form.customer,
                                                        nachname: e.target.value,
                                                    });
                                                }}
                                            />
                                            {fieldHint('customer.nachname', 'Bitte Vor- oder Nachname angeben')}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Telefon</label>
                                            <input
                                                className={INPUT_CLASS}
                                                value={form.customer.telefon}
                                                onChange={(e) =>
                                                    patch('customer', {
                                                        ...form.customer,
                                                        telefon: e.target.value,
                                                    })
                                                }
                                            />
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>E-Mail</label>
                                            <input
                                                className={INPUT_CLASS}
                                                type="email"
                                                value={form.customer.email}
                                                onChange={(e) =>
                                                    patch('customer', {
                                                        ...form.customer,
                                                        email: e.target.value,
                                                    })
                                                }
                                            />
                                        </div>
                                    </div>
                                </section>

                                <section className="space-y-3">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2">
                                        Termin &amp; Stylist
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className={LABEL_CLASS}>Datum</label>
                                            <input
                                                type="date"
                                                data-cq-field="appointment.dateIso"
                                                className={fieldClass('appointment.dateIso')}
                                                value={form.appointment.dateIso}
                                                onChange={(e) => {
                                                    clearErr('appointment.dateIso');
                                                    patch('appointment', {
                                                        ...form.appointment,
                                                        dateIso: e.target.value,
                                                    });
                                                    patch('signatures', {
                                                        ...form.signatures,
                                                        dateIso: e.target.value,
                                                    });
                                                }}
                                            />
                                            {fieldHint('appointment.dateIso', 'Bitte Datum angeben')}
                                        </div>
                                        <div className="space-y-2">
                                            <label className={LABEL_CLASS}>Stylist/in</label>
                                            <select
                                                data-cq-field="appointment.stylistName"
                                                className={fieldClass('appointment.stylistName')}
                                                value={selectedTeamEmployeeId}
                                                onChange={(e) => {
                                                    const id = e.target.value;
                                                    setSelectedTeamEmployeeId(id);
                                                    clearErr('appointment.stylistName');
                                                    if (!id) return;
                                                    const emp = teamEmployees.find((x) => x.id === id);
                                                    if (!emp) return;
                                                    patch('appointment', {
                                                        ...form.appointment,
                                                        stylistName:
                                                            emp.name === 'Ohne Name' ? '' : emp.name,
                                                    });
                                                }}
                                            >
                                                <option value="">Manuell eintragen</option>
                                                {teamEmployees.map((emp) => (
                                                    <option key={emp.id} value={emp.id}>
                                                        {emp.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <input
                                                className={fieldClass('appointment.stylistName')}
                                                value={form.appointment.stylistName}
                                                placeholder="Name Stylist/in"
                                                onChange={(e) => {
                                                    clearErr('appointment.stylistName');
                                                    const name = e.target.value;
                                                    patch('appointment', {
                                                        ...form.appointment,
                                                        stylistName: name,
                                                    });
                                                    const match = teamEmployees.find(
                                                        (x) => x.name === name.trim()
                                                    );
                                                    setSelectedTeamEmployeeId(match?.id || '');
                                                }}
                                            />
                                            {fieldHint(
                                                'appointment.stylistName',
                                                'Bitte Stylist/in auswählen'
                                            )}
                                        </div>
                                    </div>
                                </section>

                                <section className="space-y-3">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2">
                                        Haaranalyse &amp; Risiko
                                    </p>
                                    <div>
                                        <p className={LABEL_CLASS}>Ausgangsbasis</p>
                                        <div className="space-y-1.5">
                                            {CONSULT_HAIR_BASE_OPTIONS.map((o) => (
                                                <label
                                                    key={o.id}
                                                    className="flex items-center gap-2 text-sm text-pe-text dark:text-slate-200"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={form.risk.hairBase.includes(o.id)}
                                                        onChange={() =>
                                                            toggleMulti(
                                                                form.risk.hairBase,
                                                                o.id as ConsultHairBase,
                                                                (next) =>
                                                                    patch('risk', {
                                                                        ...form.risk,
                                                                        hairBase: next,
                                                                    })
                                                            )
                                                        }
                                                    />
                                                    {o.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <p className={LABEL_CLASS}>Vorbehandlungen (12 Monate)</p>
                                        <div className="space-y-1.5">
                                            {CONSULT_PRIOR_OPTIONS.map((o) => (
                                                <label
                                                    key={o.id}
                                                    className="flex items-center gap-2 text-sm text-pe-text dark:text-slate-200"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={form.risk.priorTreatments.includes(o.id)}
                                                        onChange={() =>
                                                            toggleMulti(
                                                                form.risk.priorTreatments,
                                                                o.id as ConsultPriorTreatment,
                                                                (next) =>
                                                                    patch('risk', {
                                                                        ...form.risk,
                                                                        priorTreatments: next,
                                                                    })
                                                            )
                                                        }
                                                    />
                                                    {o.label}
                                                </label>
                                            ))}
                                        </div>
                                        <label className={`${LABEL_CLASS} mt-2`}>
                                            Sonstige Vorbehandlungen / Besonderheiten
                                        </label>
                                        <textarea
                                            className={`${INPUT_CLASS} min-h-[52px]`}
                                            value={form.risk.customPriorTreatments}
                                            onChange={(e) =>
                                                patch('risk', {
                                                    ...form.risk,
                                                    customPriorTreatments: e.target.value,
                                                })
                                            }
                                        />
                                    </div>
                                    <div>
                                        <p className={LABEL_CLASS}>Haarzustand</p>
                                        <div
                                            data-cq-field="risk.hairCondition"
                                            className={`space-y-1.5 rounded-lg border px-3 py-2 ${
                                                invalidFields.has('risk.hairCondition')
                                                    ? 'border-red-500'
                                                    : 'border-pe-border dark:border-slate-700'
                                            }`}
                                        >
                                            {CONSULT_CONDITION_OPTIONS.map((o) => (
                                                <label
                                                    key={o.id}
                                                    className="flex items-center gap-2 text-sm text-pe-text dark:text-slate-200"
                                                >
                                                    <input
                                                        type="radio"
                                                        name="hairCondition"
                                                        checked={form.risk.hairCondition === o.id}
                                                        onChange={() => {
                                                            clearErr('risk.hairCondition');
                                                            patch('risk', {
                                                                ...form.risk,
                                                                hairCondition: o.id as ConsultHairCondition,
                                                            });
                                                        }}
                                                    />
                                                    {o.label}
                                                </label>
                                            ))}
                                        </div>
                                        <label className={`${LABEL_CLASS} mt-2`}>
                                            Individuelle Besonderheiten Haar/Kopfhaut
                                        </label>
                                        <textarea
                                            className={`${INPUT_CLASS} min-h-[52px]`}
                                            value={form.risk.customConditionNotes}
                                            onChange={(e) =>
                                                patch('risk', {
                                                    ...form.risk,
                                                    customConditionNotes: e.target.value,
                                                })
                                            }
                                        />
                                    </div>
                                    <div>
                                        <p className={LABEL_CLASS}>Teststrähne</p>
                                        <div
                                            data-cq-field="risk.strandTest"
                                            className={`space-y-1.5 rounded-lg border px-3 py-2 ${
                                                invalidFields.has('risk.strandTest')
                                                    ? 'border-red-500'
                                                    : 'border-pe-border dark:border-slate-700'
                                            }`}
                                        >
                                            <label className="flex items-center gap-2 text-sm text-pe-text dark:text-slate-200">
                                                <input
                                                    type="radio"
                                                    name="strandTest"
                                                    checked={form.risk.strandTest === 'ja'}
                                                    onChange={() => {
                                                        clearErr('risk.strandTest');
                                                        patch('risk', {
                                                            ...form.risk,
                                                            strandTest: 'ja' as ConsultStrandTest,
                                                        });
                                                    }}
                                                />
                                                Ja
                                            </label>
                                            <label className="flex items-center gap-2 text-sm text-pe-text dark:text-slate-200">
                                                <input
                                                    type="radio"
                                                    name="strandTest"
                                                    checked={form.risk.strandTest === 'nein_abgelehnt'}
                                                    onChange={() => {
                                                        clearErr('risk.strandTest');
                                                        patch('risk', {
                                                            ...form.risk,
                                                            strandTest: 'nein_abgelehnt' as ConsultStrandTest,
                                                        });
                                                    }}
                                                />
                                                Nein (nach Aufklärung abgelehnt)
                                            </label>
                                        </div>
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Ergebnis Teststrähne</label>
                                        <textarea
                                            className={`${INPUT_CLASS} min-h-[64px]`}
                                            value={form.risk.strandTestResult}
                                            onChange={(e) =>
                                                patch('risk', {
                                                    ...form.risk,
                                                    strandTestResult: e.target.value,
                                                })
                                            }
                                        />
                                    </div>
                                </section>

                                <section className="space-y-3">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2">
                                        Ziel &amp; Machbarkeit
                                    </p>
                                    <div>
                                        <label className={LABEL_CLASS}>Kundenwunsch</label>
                                        <textarea
                                            data-cq-field="goal.customerWish"
                                            className={`${fieldClass('goal.customerWish')} min-h-[72px]`}
                                            value={form.goal.customerWish}
                                            onChange={(e) => {
                                                clearErr('goal.customerWish');
                                                patch('goal', {
                                                    ...form.goal,
                                                    customerWish: e.target.value,
                                                });
                                            }}
                                            placeholder='z. B. "Balayage kühles Blond, Extensions 40cm"'
                                        />
                                        {fieldHint('goal.customerWish', 'Bitte Kundenwunsch angeben')}
                                    </div>
                                    <div
                                        data-cq-field="goal.feasibility"
                                        className={`space-y-1.5 rounded-lg border px-3 py-2 ${
                                            invalidFields.has('goal.feasibility')
                                                ? 'border-red-500'
                                                : 'border-pe-border dark:border-slate-700'
                                        }`}
                                    >
                                        <label className="flex items-center gap-2 text-sm text-pe-text dark:text-slate-200">
                                            <input
                                                type="radio"
                                                name="feasibility"
                                                checked={form.goal.feasibility === 'eine_sitzung'}
                                                onChange={() => {
                                                    clearErr('goal.feasibility');
                                                    patch('goal', {
                                                        ...form.goal,
                                                        feasibility: 'eine_sitzung' as ConsultFeasibility,
                                                        plannedSessions: '',
                                                    });
                                                }}
                                            />
                                            In einer Sitzung realisierbar
                                        </label>
                                        <label className="flex items-center gap-2 text-sm text-pe-text dark:text-slate-200">
                                            <input
                                                type="radio"
                                                name="feasibility"
                                                checked={form.goal.feasibility === 'mehrstufenprozess'}
                                                onChange={() => {
                                                    clearErr('goal.feasibility');
                                                    patch('goal', {
                                                        ...form.goal,
                                                        feasibility: 'mehrstufenprozess' as ConsultFeasibility,
                                                    });
                                                }}
                                            />
                                            Mehrstufenprozess erforderlich
                                        </label>
                                    </div>
                                    {form.goal.feasibility === 'mehrstufenprozess' && (
                                        <div>
                                            <label className={LABEL_CLASS}>Geplante Sitzungen</label>
                                            <input
                                                className={fieldClass('goal.plannedSessions')}
                                                value={form.goal.plannedSessions}
                                                onChange={(e) => {
                                                    clearErr('goal.plannedSessions');
                                                    patch('goal', {
                                                        ...form.goal,
                                                        plannedSessions: e.target.value,
                                                    });
                                                }}
                                                placeholder="z. B. 2"
                                            />
                                        </div>
                                    )}
                                </section>

                                <section className="space-y-3">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2">
                                        Kostenvoranschlag
                                    </p>

                                    <div className="rounded-full border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-1.5 text-[10px] text-pe-muted dark:text-slate-400 text-center leading-relaxed">
                                        Basis Barometer:{' '}
                                        {barometerRates.exclusive > 0
                                            ? `${formatEuroDe(barometerRates.exclusive)} €/h ohne Mat.`
                                            : '— €/h ohne Mat.'}{' '}
                                        |{' '}
                                        {barometerRates.inclusive > 0
                                            ? `${formatEuroDe(barometerRates.inclusive)} €/h inkl. Mat.`
                                            : '— €/h inkl. Mat.'}{' '}
                                        |{' '}
                                        {rate10g > 0
                                            ? `${formatEuroDe(rate10g)} € je 10g Mehrverbrauch`
                                            : '— € je 10g Mehrverbrauch'}
                                    </div>

                                    {(() => {
                                        const serviceItems = form.pricing.lineItems.filter(
                                            (li) => li.id !== 'heimpflege_material'
                                        );
                                        const homeItem = form.pricing.lineItems.find(
                                            (li) => li.id === 'heimpflege_material'
                                        );
                                        const renderLineCard = (
                                            item: (typeof form.pricing.lineItems)[0],
                                            idx: number
                                        ) => {
                                            const id = String(item.id);
                                            const rateMode = getRateMode(id);
                                            const isManualOnly = rateMode === 'manual';
                                            const isCustom = !isStandardLineId(id);
                                            const isBond = id === 'pflege_bondbuilder';
                                            const unit = timeUnits[id] || 'min';
                                            const canEditLabel = isManualOnly || isCustom;
                                            return (
                                                <div
                                                    key={item.id}
                                                    className="rounded-lg border border-pe-border dark:border-slate-700 p-3 space-y-2"
                                                >
                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                        {canEditLabel ? (
                                                            <input
                                                                className={`${INPUT_CLASS} flex-1 min-w-[12rem] text-xs font-semibold`}
                                                                value={item.label}
                                                                onChange={(e) =>
                                                                    handleLabelChange(id, e.target.value)
                                                                }
                                                                placeholder="Bezeichnung"
                                                            />
                                                        ) : (
                                                            <p className="text-xs font-semibold text-pe-text dark:text-slate-200">
                                                                {idx + 1}. {item.label}
                                                                <span className="ml-2 text-[9px] font-normal text-pe-muted dark:text-slate-500 uppercase tracking-wider">
                                                                    {rateMode === 'inclusive'
                                                                        ? 'inkl. Mat.'
                                                                        : 'ohne Mat.'}
                                                                </span>
                                                            </p>
                                                        )}
                                                        <div className="flex items-center gap-2">
                                                            {!isManualOnly && (
                                                                <div className="inline-flex rounded-lg border border-pe-border dark:border-slate-700 overflow-hidden">
                                                                    <button
                                                                        type="button"
                                                                        className={`px-2.5 py-1 text-[10px] font-semibold ${
                                                                            unit === 'min'
                                                                                ? 'bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299]'
                                                                                : 'text-pe-muted dark:text-slate-400'
                                                                        }`}
                                                                        onClick={() =>
                                                                            handleLineTimeUnitChange(id, 'min')
                                                                        }
                                                                    >
                                                                        Min.
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className={`px-2.5 py-1 text-[10px] font-semibold border-l border-pe-border dark:border-slate-700 ${
                                                                            unit === 'std'
                                                                                ? 'bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299]'
                                                                                : 'text-pe-muted dark:text-slate-400'
                                                                        }`}
                                                                        onClick={() =>
                                                                            handleLineTimeUnitChange(id, 'std')
                                                                        }
                                                                    >
                                                                        Std.
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {isCustom && (
                                                                <button
                                                                    type="button"
                                                                    className="text-[10px] text-red-400 underline"
                                                                    onClick={() => handleRemoveCustomLine(id)}
                                                                >
                                                                    Entfernen
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {canEditLabel && (
                                                        <p className="text-[10px] text-pe-muted dark:text-slate-500">
                                                            Pos. {idx + 1}
                                                            {isManualOnly ? ' · Heimpflege / Mitnahme (Verkauf)' : ' · Zusatzleistung'}
                                                        </p>
                                                    )}
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                        <div>
                                                            <label className={LABEL_CLASS}>
                                                                {isManualOnly
                                                                    ? 'Menge / Hinweis'
                                                                    : `Zeitaufwand (${unit === 'min' ? 'Min.' : 'Std.'})`}
                                                            </label>
                                                            {isManualOnly ? (
                                                                <input
                                                                    className={INPUT_CLASS}
                                                                    value={timeInputs[id] || ''}
                                                                    disabled={Boolean(item.inclusive)}
                                                                    onChange={(e) =>
                                                                        handleTimeInputChange(id, e.target.value)
                                                                    }
                                                                    placeholder={
                                                                        item.inclusive
                                                                            ? '—'
                                                                            : 'z. B. 1× Flasche'
                                                                    }
                                                                />
                                                            ) : (
                                                                <input
                                                                    className={INPUT_CLASS}
                                                                    type="number"
                                                                    min="0"
                                                                    step={unit === 'min' ? '1' : '0.25'}
                                                                    value={timeInputs[id] || ''}
                                                                    onChange={(e) =>
                                                                        handleTimeInputChange(id, e.target.value)
                                                                    }
                                                                    placeholder={
                                                                        unit === 'min' ? 'z. B. 45' : 'z. B. 1,5'
                                                                    }
                                                                />
                                                            )}
                                                            {!isManualOnly && item.timeEffort ? (
                                                                <p className="mt-1 text-[10px] text-pe-muted dark:text-slate-500">
                                                                    Druck: {item.timeEffort}
                                                                </p>
                                                            ) : null}
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center justify-between gap-2">
                                                                <label className={LABEL_CLASS}>
                                                                    Betrag EUR inkl. MwSt.
                                                                </label>
                                                                {!isManualOnly && amountManual[id] && (
                                                                    <button
                                                                        type="button"
                                                                        className="text-[10px] text-pe-accent-text dark:text-[#E6C299] underline"
                                                                        onClick={() => handleRecalcLine(id)}
                                                                    >
                                                                        Neu berechnen
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <input
                                                                className={INPUT_CLASS}
                                                                value={
                                                                    item.inclusive ? '' : item.amountEur
                                                                }
                                                                disabled={Boolean(item.inclusive)}
                                                                onChange={(e) =>
                                                                    handleAmountChange(id, e.target.value)
                                                                }
                                                                placeholder={
                                                                    item.inclusive
                                                                        ? 'inkl.'
                                                                        : isManualOnly
                                                                          ? 'Verkaufspreis'
                                                                          : 'z. B. 89'
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    {isBond && (
                                                        <div className="pt-2 space-y-2 border-t border-pe-border dark:border-slate-700">
                                                            <label className={LABEL_CLASS}>
                                                                Bond-Produkt (was angewendet wurde)
                                                            </label>
                                                            <input
                                                                className={INPUT_CLASS}
                                                                value={form.pricing.bondProductName || ''}
                                                                onChange={(e) =>
                                                                    patchBondField({
                                                                        bondProductName: e.target.value,
                                                                    })
                                                                }
                                                                placeholder="z. B. Olaplex / K18"
                                                            />
                                                            <label className="flex items-center gap-2 text-xs text-pe-text dark:text-slate-200 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={
                                                                        form.pricing.bondProductInclusive !==
                                                                        false
                                                                    }
                                                                    onChange={(e) =>
                                                                        patchBondField({
                                                                            bondProductInclusive:
                                                                                e.target.checked,
                                                                        })
                                                                    }
                                                                />
                                                                Inklusive (kein Extra-Preis, nur erwähnt)
                                                            </label>
                                                            {!form.pricing.bondProductInclusive && (
                                                                <div>
                                                                    <label className={LABEL_CLASS}>
                                                                        Extra-Preis EUR (wird zugerechnet)
                                                                    </label>
                                                                    <input
                                                                        className={INPUT_CLASS}
                                                                        value={
                                                                            form.pricing
                                                                                .bondProductAmountEur || ''
                                                                        }
                                                                        onChange={(e) =>
                                                                            patchBondField({
                                                                                bondProductAmountEur:
                                                                                    e.target.value,
                                                                            })
                                                                        }
                                                                        placeholder="z. B. 25"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {isManualOnly && (
                                                        <label className="flex items-center gap-2 text-xs text-pe-text dark:text-slate-200 cursor-pointer pt-1">
                                                            <input
                                                                type="checkbox"
                                                                checked={Boolean(item.inclusive)}
                                                                onChange={(e) =>
                                                                    setLineInclusive(id, e.target.checked)
                                                                }
                                                            />
                                                            Inklusive (kein Extra-Preis, nur erwähnt)
                                                        </label>
                                                    )}
                                                </div>
                                            );
                                        };

                                        return (
                                            <>
                                                {serviceItems.map((item, idx) => renderLineCard(item, idx))}
                                                <button
                                                    type="button"
                                                    onClick={handleAddCustomLine}
                                                    className="w-full rounded-lg border border-dashed border-pe-border dark:border-slate-600 px-3 py-2.5 text-xs font-semibold text-pe-accent-text dark:text-[#E6C299] hover:bg-pe-hover dark:hover:bg-[#1a1a1a] transition-colors"
                                                >
                                                    + Weitere Leistung (nach den Leistungen)
                                                </button>
                                                {homeItem
                                                    ? renderLineCard(homeItem, serviceItems.length)
                                                    : null}
                                            </>
                                        );
                                    })()}

                                    <div className="rounded-lg border border-pe-border dark:border-slate-700 p-3 space-y-2">
                                        <p className="text-xs font-semibold text-pe-text dark:text-slate-200">
                                            Verkaufsprodukte / Mitnahme
                                        </p>
                                        <p className="text-[10px] text-pe-muted dark:text-slate-500">
                                            Name eintragen. Haken „Inklusive“ = nicht berechnet. Ohne Haken =
                                            Preis wird zugerechnet.
                                        </p>
                                        {(form.pricing.salesProducts || []).map((sp) => (
                                            <div key={sp.id} className="space-y-2 rounded-lg border border-pe-border dark:border-slate-700 p-2">
                                                <input
                                                    className={INPUT_CLASS}
                                                    value={sp.label}
                                                    onChange={(e) =>
                                                        updateSalesProduct(sp.id, {
                                                            label: e.target.value,
                                                        })
                                                    }
                                                    placeholder="Produktname"
                                                />
                                                <div className="flex flex-wrap items-center gap-3">
                                                    <label className="flex items-center gap-2 text-xs text-pe-text dark:text-slate-200 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={sp.inclusive}
                                                            onChange={(e) =>
                                                                updateSalesProduct(sp.id, {
                                                                    inclusive: e.target.checked,
                                                                    amountEur: e.target.checked
                                                                        ? ''
                                                                        : sp.amountEur,
                                                                })
                                                            }
                                                        />
                                                        Inklusive
                                                    </label>
                                                    {!sp.inclusive && (
                                                        <input
                                                            className={`${INPUT_CLASS} max-w-[8rem]`}
                                                            value={sp.amountEur}
                                                            onChange={(e) =>
                                                                updateSalesProduct(sp.id, {
                                                                    amountEur: e.target.value,
                                                                })
                                                            }
                                                            placeholder="EUR"
                                                        />
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="text-[10px] text-red-400 underline ml-auto"
                                                        onClick={() => removeSalesProduct(sp.id)}
                                                    >
                                                        Entfernen
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={addSalesProduct}
                                            className="w-full rounded-lg border border-dashed border-pe-border dark:border-slate-600 px-3 py-2 text-xs font-semibold text-pe-accent-text dark:text-[#E6C299]"
                                        >
                                            + Verkaufsprodukt eintragen
                                        </button>
                                    </div>

                                    <div>
                                        <label className={LABEL_CLASS}>
                                            Zusatzmaterial je 10g (EUR)
                                        </label>
                                        <input
                                            className={INPUT_CLASS}
                                            value={form.pricing.materialSurchargePer10g}
                                            onChange={(e) =>
                                                patch('pricing', {
                                                    ...form.pricing,
                                                    materialSurchargePer10g: e.target.value,
                                                })
                                            }
                                            placeholder="z. B. 8,50"
                                        />
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between gap-2">
                                            <label className={LABEL_CLASS}>
                                                Kalkulierter Gesamtbetrag (EUR)
                                            </label>
                                            {totalManual && (
                                                <button
                                                    type="button"
                                                    className="text-[10px] text-pe-accent-text dark:text-[#E6C299] underline"
                                                    onClick={handleRecalcTotal}
                                                >
                                                    Neu berechnen
                                                </button>
                                            )}
                                        </div>
                                        <input
                                            data-cq-field="pricing.totalEur"
                                            className={fieldClass('pricing.totalEur')}
                                            value={form.pricing.totalEur}
                                            onChange={(e) => handleTotalChange(e.target.value)}
                                        />
                                        {fieldHint('pricing.totalEur', 'Bitte Gesamtbetrag angeben')}
                                    </div>

                                    <div className="rounded-xl border border-[#E6C299]/35 bg-pe-input/40 dark:bg-black/30 p-3 space-y-3">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-pe-muted dark:text-slate-500">
                                            Zusatzvereinbarungen / Optionen
                                        </p>
                                        <label className="flex items-start gap-2.5 cursor-pointer select-none">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(form.pricing.hasPflegeGarantie)}
                                                onChange={(e) =>
                                                    patchPflegeGarantie({
                                                        hasPflegeGarantie: e.target.checked,
                                                    })
                                                }
                                                className="w-5 h-5 mt-0.5 shrink-0 cursor-pointer appearance-none bg-pe-input dark:bg-black border-2 border-[#E6C299]/60 rounded-md checked:bg-[#E6C299] checked:border-[#E6C299] relative transition-colors after:content-['✔'] after:absolute after:text-black after:text-[11px] after:font-black after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:opacity-0 checked:after:opacity-100"
                                            />
                                            <span className="text-xs font-bold text-pe-text dark:text-slate-200 leading-snug">
                                                Freiwillige Farbfrische- &amp; Haltbarkeitsgarantie
                                                aktivieren
                                            </span>
                                        </label>

                                        {form.pricing.hasPflegeGarantie ? (
                                            <div className="space-y-3 pl-0.5">
                                                <div>
                                                    <label className={LABEL_CLASS}>
                                                        Garantiezeitraum (Wochen)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        step={1}
                                                        className={`${INPUT_CLASS} max-w-[8rem]`}
                                                        value={
                                                            Number.isFinite(form.pricing.garantieWochen)
                                                                ? form.pricing.garantieWochen
                                                                : 4
                                                        }
                                                        onChange={(e) => {
                                                            const w = Number(e.target.value);
                                                            patchPflegeGarantie({
                                                                garantieWochen:
                                                                    Number.isFinite(w) && w > 0
                                                                        ? Math.round(w)
                                                                        : 4,
                                                            });
                                                        }}
                                                    />
                                                </div>

                                                <label className="flex items-center gap-2 text-xs text-pe-text dark:text-slate-200 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(
                                                            form.pricing.garantiePflegesetInGesamtpreis
                                                        )}
                                                        onChange={(e) =>
                                                            patchPflegeGarantie({
                                                                garantiePflegesetInGesamtpreis:
                                                                    e.target.checked,
                                                            })
                                                        }
                                                    />
                                                    Als Pflegeset in Gesamtpreis einrechnen
                                                </label>

                                                <div className="space-y-2">
                                                    <p className="text-xs font-semibold text-pe-text dark:text-slate-200">
                                                        Vereinbarte Pflegeprodukte
                                                    </p>
                                                    <p className="text-[10px] text-pe-muted dark:text-slate-500">
                                                        Produktname, empfohlene Anwendung/Routine und
                                                        Bruttopreis eintragen.
                                                    </p>
                                                    {(form.pricing.garantieProdukte || []).map((gp) => (
                                                        <div
                                                            key={gp.id}
                                                            className="space-y-2 rounded-lg border border-pe-border dark:border-slate-700 p-2"
                                                        >
                                                            <input
                                                                className={INPUT_CLASS}
                                                                value={gp.name}
                                                                onChange={(e) =>
                                                                    updateGarantieProdukt(gp.id, {
                                                                        name: e.target.value,
                                                                    })
                                                                }
                                                                placeholder="Produktname"
                                                            />
                                                            <input
                                                                className={INPUT_CLASS}
                                                                value={gp.anwendung}
                                                                onChange={(e) =>
                                                                    updateGarantieProdukt(gp.id, {
                                                                        anwendung: e.target.value,
                                                                    })
                                                                }
                                                                placeholder="Anwendung, z. B. 1x wöchentlich Silver-Maske"
                                                            />
                                                            <div className="flex flex-wrap items-center gap-3">
                                                                <input
                                                                    type="number"
                                                                    min={0}
                                                                    step={0.01}
                                                                    className={`${INPUT_CLASS} max-w-[8rem]`}
                                                                    value={
                                                                        Number.isFinite(gp.preisBrutto) &&
                                                                        gp.preisBrutto > 0
                                                                            ? gp.preisBrutto
                                                                            : gp.preisBrutto === 0
                                                                              ? ''
                                                                              : gp.preisBrutto
                                                                    }
                                                                    onChange={(e) => {
                                                                        const n = Number(e.target.value);
                                                                        updateGarantieProdukt(gp.id, {
                                                                            preisBrutto:
                                                                                Number.isFinite(n) && n >= 0
                                                                                    ? n
                                                                                    : 0,
                                                                        });
                                                                    }}
                                                                    placeholder="EUR brutto"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="text-[10px] text-red-400 underline ml-auto"
                                                                    onClick={() =>
                                                                        removeGarantieProdukt(gp.id)
                                                                    }
                                                                >
                                                                    Entfernen
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={addGarantieProdukt}
                                                        className="w-full rounded-lg border border-dashed border-pe-border dark:border-slate-600 px-3 py-2 text-xs font-semibold text-pe-accent-text dark:text-[#E6C299]"
                                                    >
                                                        + Pflegeprodukt eintragen
                                                    </button>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="rounded-xl border border-[#E6C299]/35 bg-pe-input/40 dark:bg-black/30 p-3 space-y-3">
                                        <label className="flex items-center gap-2.5 cursor-pointer select-none">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(depositCfg.anzahlungAktiv)}
                                                onChange={(e) =>
                                                    patchDeposit({ anzahlungAktiv: e.target.checked })
                                                }
                                                className="w-5 h-5 shrink-0 cursor-pointer appearance-none bg-pe-input dark:bg-black border-2 border-[#E6C299]/60 rounded-md checked:bg-[#E6C299] checked:border-[#E6C299] relative transition-colors after:content-['✔'] after:absolute after:text-black after:text-[11px] after:font-black after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:opacity-0 checked:after:opacity-100"
                                            />
                                            <span className="text-xs font-bold uppercase tracking-wider text-pe-text dark:text-slate-200">
                                                Anzahlung vereinbaren
                                            </span>
                                        </label>

                                        {depositCfg.anzahlungAktiv ? (
                                            <div className="space-y-3 pl-0.5">
                                                <div>
                                                    <p className={LABEL_CLASS}>Anzahlungstyp</p>
                                                    <div className="inline-flex rounded-lg border border-[#E6C299]/40 overflow-hidden">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                patchDeposit({ anzahlungTyp: 'prozent' })
                                                            }
                                                            className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide transition-colors ${
                                                                depositCfg.anzahlungTyp === 'prozent'
                                                                    ? 'bg-[#E6C299] text-black'
                                                                    : 'bg-transparent text-pe-muted dark:text-slate-400 hover:text-pe-text'
                                                            }`}
                                                        >
                                                            % (Prozent)
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                patchDeposit({
                                                                    anzahlungTyp: 'festbetrag',
                                                                })
                                                            }
                                                            className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide transition-colors border-l border-[#E6C299]/40 ${
                                                                depositCfg.anzahlungTyp === 'festbetrag'
                                                                    ? 'bg-[#E6C299] text-black'
                                                                    : 'bg-transparent text-pe-muted dark:text-slate-400 hover:text-pe-text'
                                                            }`}
                                                        >
                                                            € (Festbetrag)
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    <div>
                                                        <label className={LABEL_CLASS}>
                                                            {depositCfg.anzahlungTyp === 'festbetrag'
                                                                ? 'Festbetrag (EUR)'
                                                                : 'Anteil (%)'}
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            step={depositCfg.anzahlungTyp === 'prozent' ? 1 : 0.01}
                                                            className={INPUT_CLASS}
                                                            value={
                                                                Number.isFinite(depositCfg.anzahlungWert)
                                                                    ? depositCfg.anzahlungWert
                                                                    : ''
                                                            }
                                                            onChange={(e) =>
                                                                patchDeposit({
                                                                    anzahlungWert: Number(
                                                                        e.target.value
                                                                    ),
                                                                })
                                                            }
                                                        />
                                                        <p className="mt-1 text-[11px] text-pe-muted dark:text-slate-500">
                                                            = {formatEuroDe(depositAmountEur)} EUR Anzahlung
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <label className={LABEL_CLASS}>
                                                            Stornofrist (Stunden)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            step={1}
                                                            className={INPUT_CLASS}
                                                            value={
                                                                Number.isFinite(
                                                                    depositCfg.stornoFristStunden
                                                                )
                                                                    ? depositCfg.stornoFristStunden
                                                                    : 48
                                                            }
                                                            onChange={(e) =>
                                                                patchDeposit({
                                                                    stornoFristStunden: Number(
                                                                        e.target.value
                                                                    ),
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className={LABEL_CLASS}>Ort (Unterschrift)</label>
                                            <input
                                                className={INPUT_CLASS}
                                                value={form.signatures.place}
                                                onChange={(e) =>
                                                    patch('signatures', {
                                                        ...form.signatures,
                                                        place: e.target.value,
                                                    })
                                                }
                                            />
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Datum (Unterschrift)</label>
                                            <input
                                                type="date"
                                                className={INPUT_CLASS}
                                                value={form.signatures.dateIso}
                                                onChange={(e) =>
                                                    patch('signatures', {
                                                        ...form.signatures,
                                                        dateIso: e.target.value,
                                                    })
                                                }
                                            />
                                        </div>
                                    </div>
                                </section>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="w-full overflow-visible pr-1 pb-8">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-pe-muted dark:text-slate-500 mb-3 print:hidden">
                                    Vorschau — 2 A4-Seiten
                                </p>
                                {renderDocument()}
                            </div>
                        )}
                    </div>

                    {currentStep === 2 && (
                        <div className="px-4 sm:px-6 py-3 shrink-0 print:hidden border-t border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#111] relative z-10">
                            <div
                                className={`rounded-xl border-2 transition-all duration-200 ${
                                    disclaimerAccepted
                                        ? 'border-emerald-500/40 bg-emerald-500/5 px-3 py-2.5'
                                        : 'border-amber-500 bg-amber-500/15 dark:bg-amber-500/10 shadow-[0_0_24px_rgba(245,158,11,0.25)] p-3 sm:p-4'
                                }`}
                            >
                                {!disclaimerAccepted && (
                                    <>
                                        <p className="mb-1 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-amber-800 dark:text-amber-300">
                                            Pflicht · Rechtlicher Hinweis bestätigen
                                        </p>
                                        <p className="mb-2 text-[11px] sm:text-xs font-semibold text-amber-950 dark:text-amber-100/95 leading-snug">
                                            Ohne diese Bestätigung bleiben PDF-Download, Druck und Archiv-Speichern
                                            gesperrt.
                                        </p>
                                    </>
                                )}
                                <label className="flex items-start gap-3 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={disclaimerAccepted}
                                        onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                                        className="mt-0.5 w-7 h-7 sm:w-6 sm:h-6 shrink-0 cursor-pointer appearance-none bg-pe-input dark:bg-black border-2 border-amber-600 dark:border-amber-400 rounded-md checked:bg-[#E6C299] checked:border-[#E6C299] relative transition-colors after:content-['✔'] after:absolute after:text-black after:text-[14px] after:font-black after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:opacity-0 checked:after:opacity-100"
                                    />
                                    <span
                                        className={
                                            disclaimerAccepted
                                                ? 'text-sm font-semibold text-emerald-800 dark:text-emerald-300 leading-snug'
                                                : 'text-sm text-pe-text dark:text-slate-200 leading-snug'
                                        }
                                    >
                                        {disclaimerAccepted
                                            ? 'Rechtlicher Hinweis bestätigt'
                                            : 'Ich habe die Daten geprüft. Es handelt sich um ein Standard-Muster ohne individuelle Rechtsberatung; Verwendung auf eigene Verantwortung.'}
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}

                    <div className="px-6 py-4 border-t border-pe-border dark:border-[#222] shrink-0 print:hidden">
                        {currentStep === 1 && validationBanner && (
                            <p className="mb-3 text-center text-xs font-semibold text-red-400">
                                Bitte fülle die markierten Pflichtfelder aus.
                            </p>
                        )}
                        <div className="flex flex-wrap gap-3">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(1)}
                                    className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300"
                                >
                                    Zurück zum Formular
                                </button>
                            )}
                            {currentStep === 1 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 disabled:opacity-50"
                                    >
                                        {archiveFeedback === 'draft'
                                            ? 'Gespeichert! ✓'
                                            : isSaving
                                              ? 'Speichert...'
                                              : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleWeiter}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-[#E6C299] hover:bg-[#d9b183] text-black"
                                    >
                                        Dokument erstellen
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(false)}
                                        disabled={isActionLocked || isSaving}
                                        className={btnClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299]'
                                        )}
                                    >
                                        {archiveFeedback === 'success' ? 'Gespeichert! ✓' : 'Im Archiv speichern'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleDownloadPdf()}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={btnClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-gradient-to-b from-[#E6C299] to-[#b48a56] text-black'
                                        )}
                                    >
                                        {isPdfDownloading ? 'PDF wird erstellt...' : 'Als PDF herunterladen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handlePrint}
                                        disabled={isActionLocked}
                                        className={btnClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299]'
                                        )}
                                    >
                                        Drucken
                                    </button>
                                </>
                            )}
                        </div>
                        {archiveFeedback === 'error' && (
                            <p className="text-[10px] text-red-400 mt-2 text-center">
                                Archivierung fehlgeschlagen. Bitte erneut versuchen.
                                {archiveErrorDetail ? (
                                    <>
                                        <br />
                                        <span className="opacity-80">{archiveErrorDetail}</span>
                                    </>
                                ) : null}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default ConsultationQuoteModal;
